import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';
import api from '../services/api';
import * as XLSX from 'xlsx-js-style';

/* ── Palette Décisionnel ─────────────────────────────────────────── */
const C = {
  primary: '#2d7a2d',
  light:   '#f1f8f1',
  border:  '#c8dfc8',
};

const TH = {
  padding: '7px 10px',
  fontSize: '0.67rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#4a505c',
  background: '#f5f4f2',
  borderBottom: '2px solid #d4c8bc',
  whiteSpace: 'nowrap',
};
const TD = {
  padding: '7px 10px',
  fontSize: '0.76rem',
  color: '#333',
  borderBottom: '1px solid #ede9e4',
  verticalAlign: 'middle',
};

const TYPE_COLORS = ['#1565c0','#2e7d32','#6a1b9a','#e65100','#6d4e36','#c62828','#546e7a','#00796b','#ad1457','#283593'];
const typeColor = (i) => TYPE_COLORS[i % TYPE_COLORS.length];

const STATUT_NOTAIRE_CFG = {
  NOT_YET:  { label: 'Pas encore signé',   color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  CHEZ:     { label: 'Signé client',        color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  EN_COURS: { label: 'Signature en cours',  color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  SIGNE:    { label: 'Signé notaire',       color: '#1b5e20', bg: '#e8f5e9', border: '#a5d6a7' },
};

const STATUT_CFG = {
  LIBRE:            { color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  RESERVE:          { color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  VENDU:            { color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  'SIGNE NOTAIRE':  { color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  BLOQUE:           { color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
};
const STATUT_FALLBACK = { color: '#546e7a', bg: '#eceff1', border: '#b0bec5' };

const PAGE_SIZES = [10, 25, 50, 100];

const DATE_FIELD_MAP = {
  'sign-client':  'date_signature_client',
  'en-cours':     'date_en_cours',
  'sign-notaire': 'date_signature_notaire',
  'minute':       'date_minute',
  'expedition':   'date_expedition',
  'compta':       'date_compta',
};

const getMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const pad = n => String(n).padStart(2, '0');
  const debut = `${y}-${pad(m + 1)}-01`;
  const fin   = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;
  return { debut, fin, active: false };
};

const getYearRange = () => {
  const y = new Date().getFullYear();
  return { debut: `${y}-01-01`, fin: `${y}-12-31`, active: false };
};

/* ── Paramètres par défaut du CA comptable pour une année d'exercice ── */
const getCaComptaDefaults = (annee) => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return {
    annee,
    nDebut:  `${annee}-01-01`,
    nFin:    annee === now.getFullYear() ? today : `${annee}-12-31`,
    n1Debut: `${annee - 1}-01-01`,
    n1Fin:   `${annee - 1}-12-31`,
  };
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtN = (n) =>
  Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── Colore la 1ère ligne (en-tête) en vert/blanc + active l'autofiltre ── */
const styleExcelHeader = (ws) => {
  const ref = ws['!ref'];
  if (!ref) return ws;
  const range = XLSX.utils.decode_range(ref);
  for (let col = range.s.c; col <= range.e.c; col++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '2D7A2D' } },
    };
  }
  ws['!autofilter'] = { ref };
  return ws;
};

/* ── Agrège une liste de rows detail par clé ────────────────────── */
const aggregate = (rows) => {
  const map = {};
  rows.forEach(r => {
    if (!map[r.tranche]) map[r.tranche] = { tranche: r.tranche, nb_biens: 0, superficie_totale: 0 };
    map[r.tranche].nb_biens        += Number(r.nb_biens);
    map[r.tranche].superficie_totale += Number(r.superficie_totale);
  });
  return Object.values(map).sort((a, b) => a.tranche.localeCompare(b.tranche, 'fr'));
};

/* ── Toggle échelle des montants (Auto / MAD / kMAD / MMAD) ──────── */
const UNIT_OPTIONS = [['auto', 'Auto'], ['MAD', 'MAD'], ['kMAD', 'kMAD'], ['MMAD', 'MMAD']];
const UnitToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {UNIT_OPTIONS.map(([key, label]) => {
      const active = value === key;
      return (
        <button
          key={key}
          onClick={() => onChange(key)}
          style={{
            padding: '2px 7px', fontSize: '0.6rem', fontWeight: 700,
            border: `1px solid ${active ? '#4a505c' : '#d0c8bc'}`,
            background: active ? '#4a505c' : '#f7f5f2',
            color: active ? '#fff' : '#999',
            borderRadius: 4, cursor: 'pointer', lineHeight: 1.6,
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

/* ── Card cliquable ─────────────────────────────────────────────── */
const FilterCard = ({ label, value, color, bg, border, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: '1 1 140px',
      background: active ? color : (bg || '#fff'),
      border: `2px solid ${active ? color : (border || '#d4c8bc')}`,
      borderRadius: 8,
      borderTop: `3px solid ${color}`,
      padding: '10px 14px',
      minWidth: 0,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.15s',
      boxShadow: active ? `0 0 0 3px ${color}33` : 'none',
    }}
  >
    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: active ? 'rgba(255,255,255,0.75)' : '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: 700, color: active ? '#fff' : color }}>{value}</div>
  </button>
);

const SectionHeader = ({ label, open, onToggle, extra, selectedCount = 0 }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '13px 16px',
      background: open ? '#f4fbf4' : '#e8f5e9',
      borderLeft: '4px solid #2d7a2d',
      cursor: 'pointer', userSelect: 'none',
      transition: 'background 0.2s',
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#2d7a2d', textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      {!open && (
        <div style={{ fontSize: '0.6rem', color: '#7aaa7a', marginTop: 2 }}>
          Cliquez pour développer
        </div>
      )}
    </div>
    {selectedCount > 0 && (
      <span style={{
        fontSize: '0.62rem', fontWeight: 700, color: '#fff', flexShrink: 0,
        background: '#2d7a2d', borderRadius: 12, padding: '3px 10px',
        boxShadow: '0 1px 4px rgba(45,122,45,0.3)',
      }}>
        {selectedCount} filtre{selectedCount > 1 ? 's' : ''} actif{selectedCount > 1 ? 's' : ''}
      </span>
    )}
    {extra}
    <span style={{
      fontSize: '1.1rem', color: '#2d7a2d', fontWeight: 700, flexShrink: 0,
      display: 'inline-block',
      transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.25s',
      lineHeight: 1,
    }}>
      ›
    </span>
  </div>
);

/* ── En-tête pliable pour une sous-section de filtre ─────────────── */
const SubFilterHeader = ({ label, open, count = 0, onToggle, extra }) => (
  <div
    onClick={onToggle}
    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}
  >
    <span style={{
      fontSize: '0.8rem', color: '#2d7a2d', fontWeight: 700, display: 'inline-block',
      transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', lineHeight: 1,
    }}>
      ›
    </span>
    <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.8 }}>
      {label}{count > 0 && <span style={{ marginLeft: 6, color: '#2d7a2d' }}>· {count} sél.</span>}
    </span>
    {extra}
  </div>
);

const SortTH = ({ col, label, align = 'left', sortCol, sortDir, onSort, style = {} }) => {
  const active = sortCol === col;
  return (
    <th onClick={() => onSort(col)} style={{ ...TH, textAlign: align, cursor: 'pointer', userSelect: 'none', ...style }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {label}
        <span style={{ fontSize: '0.55rem', color: active ? '#4a505c' : '#d4c8bc' }}>
          {active ? (sortDir === 'asc' ? '▴' : '▾') : '⇅'}
        </span>
      </span>
    </th>
  );
};

const TrancheChip = ({ label, value, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
      border: `1.5px solid ${active ? C.primary : C.border}`,
      background: active ? C.primary : '#fff',
      color: active ? '#fff' : '#4a505c',
      fontSize: '0.72rem', fontWeight: 600,
      transition: 'all 0.15s',
      boxShadow: active ? `0 0 0 2px ${C.primary}33` : 'none',
    }}
  >
    {label}
    <span style={{
      fontSize: '0.62rem', fontWeight: 700, borderRadius: 10, padding: '0 5px',
      background: active ? 'rgba(255,255,255,0.25)' : C.light,
      color: active ? '#fff' : C.primary,
    }}>
      {value}
    </span>
  </button>
);

/* ── Popup détail des biens (style KpiDetailPanel du Décisionnel) ── */
const BienDetailPopup = ({ detail, showProjet, onClose }) => {
  const [sortKey, setSortKey] = useState('');
  const [asc, setAsc]         = useState(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cols = [
    { key: 'id', label: 'ID Bien' },
    { key: 'numero', label: 'N°' },
    ...(showProjet ? [{ key: 'projet_nom', label: 'Projet' }] : []),
    { key: 'contrat_id', label: 'ID Vente' },
    { key: 'tranche', label: 'Tranche' },
    { key: 'designation', label: 'Désignation' },
    { key: 'type', label: 'Type' },
    { key: 'superficie', label: 'Superficie (m²)', right: true, num: true },
    { key: 'ca', label: 'CA', right: true, num: true },
    { key: 'cout_m2', label: 'Coût/m²', right: true, num: true },
    { key: 'cout_total', label: 'Coût total', right: true, num: true },
    { key: 'marge', label: 'Marge', right: true, num: true },
    { key: 'marge_pct', label: '%', right: true, num: true },
    { key: 'date_signature_notaire', label: 'Signé notaire', date: true },
    { key: 'date_compta', label: 'Date compta', date: true },
    { key: 'notaire', label: 'Notaire' },
  ];

  const rows = useMemo(() => {
    if (!sortKey) return detail.rows;
    return [...detail.rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null || av === '') return 1;
      if (bv == null || bv === '') return -1;
      const na = Number(av), nb = Number(bv);
      const cmp = (!isNaN(na) && !isNaN(nb)) ? na - nb : String(av).localeCompare(String(bv), 'fr');
      return asc ? cmp : -cmp;
    });
  }, [detail.rows, sortKey, asc]);

  const onSort = (k) => { if (sortKey === k) setAsc(a => !a); else { setSortKey(k); setAsc(true); } };
  const totalCa  = rows.reduce((s, r) => s + (Number(r.ca) || 0), 0);
  const totalSup = rows.reduce((s, r) => s + (Number(r.superficie) || 0), 0);
  const totalCout  = rows.reduce((s, r) => s + (Number(r.cout_total) || 0), 0);
  const totalMarge = rows.reduce((s, r) => s + (Number(r.marge) || 0), 0);
  const cell = (r, c) => c.key === 'contrat_id'
      ? (r.contrat_id
          ? <a href={`https://shems.ngrok.io/consultation/${r.contrat_id}`} target="_blank" rel="noreferrer"
              style={{ color: C.primary, fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed currentColor' }}>#{r.contrat_id}</a>
          : '—')
    : c.date ? fmtDate(r[c.key])
    : c.key === 'marge_pct' ? (r[c.key] != null ? `${r[c.key].toFixed(1)}%` : '—')
    : c.num ? (r[c.key] != null ? fmtN(r[c.key]) : '—')
    : (r[c.key] || '—');

  const handleExport = () => {
    const data = rows.map(r => Object.fromEntries(cols.map(c => [c.label, c.date ? (r[c.key] ? fmtDate(r[c.key]) : '') : c.num ? (r[c.key] != null ? Number(r[c.key]) : '') : (r[c.key] ?? '')])));
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(data));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detail');
    XLSX.writeFile(wb, `${detail.title.replace(/[^\w\d]+/g, '_')}.xlsx`);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '8px 8px 0' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px 12px 0 0', width: '100%', maxWidth: 1300, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: C.light, borderBottom: `1px solid ${C.border}`, flexShrink: 0, gap: 8 }}>
          <div style={{ fontWeight: 700, color: C.primary, fontSize: '0.88rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail.title}
            <span style={{ marginLeft: 8, background: '#fff', color: C.primary, fontSize: '0.65rem', border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 6px' }}>
              {rows.length} ligne{rows.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {rows.length > 0 && (
              <button onClick={handleExport} style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', borderRadius: 6, padding: '6px 12px', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>↓ Excel</button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #ddd', cursor: 'pointer', color: '#888', fontSize: '1rem', lineHeight: 1, padding: '8px 12px', borderRadius: 6, minWidth: 40, minHeight: 40 }} title="Fermer">✕</button>
          </div>
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 50, textAlign: 'center', color: '#aaa', fontSize: '0.9rem' }}>Aucune donnée</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  {cols.map(c => (
                    <SortTH key={c.key} col={c.key} label={c.label} align={c.right ? 'right' : 'left'} sortCol={sortKey} sortDir={asc ? 'asc' : 'desc'} onSort={onSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.id}-${r.contrat_id}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#faf9f8' }}>
                    {cols.map(c => (
                      <td key={c.key} style={{ ...TD, textAlign: c.right ? 'right' : 'left', whiteSpace: c.date || c.num ? 'nowrap' : undefined, fontWeight: c.key === 'ca' ? 700 : 400, color: c.key === 'ca' ? C.primary : TD.color }}>
                        {cell(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: C.light, borderTop: `2px solid ${C.border}` }}>
                  {cols.map((c, j) => (
                    <td key={c.key} style={{ ...TD, textAlign: 'right', fontWeight: 800, color: C.primary, whiteSpace: 'nowrap' }}>
                      {j === 0 ? 'TOTAL'
                        : c.key === 'ca' ? fmtN(totalCa)
                        : c.key === 'superficie' ? fmtN(totalSup)
                        : c.key === 'cout_total' ? fmtN(totalCout)
                        : c.key === 'marge' ? fmtN(totalMarge)
                        : c.key === 'marge_pct' && totalCa > 0 && totalCout > 0 ? `${((totalMarge / totalCa) * 100).toFixed(1)}%`
                        : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default function BienTranchePage() {
  const [projets, setProjets]     = useState([]);
  const [selectedProjetIds, setSelectedProjetIds] = useState([]);
  const isMultiProjet = selectedProjetIds.length > 1;
  const [detail, setDetail]       = useState([]);
  const [biens, setBiens]         = useState([]);
  const [couts, setCouts]         = useState({});
  const [coutsType, setCoutsType] = useState({}); // surcharges coût/m² par type dans une tranche
  const [expandedTranches, setExpandedTranches] = useState(new Set()); // tranches dépliées « par type »
  const [expandedCaTranches, setExpandedCaTranches] = useState(new Set()); // idem, tableau CA par tranche
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const [saveMsg, setSaveMsg]     = useState('');

  /* filtres actifs */
  const [activeStatuts, setActiveStatuts]           = useState(new Set());
  const [activeTypes, setActiveTypes]               = useState(new Set());
  const [activeTranches, setActiveTranches]         = useState(new Set());
  const [activeTranchesMois, setActiveTranchesMois] = useState(new Set());
  const [activeStatutNotaire, setActiveStatutNotaire] = useState(new Set());
  const [activeNotaires, setActiveNotaires]         = useState(new Set());
  const [activeAnneesCompta, setActiveAnneesCompta] = useState(new Set());
  const [unitScale, setUnitScale]                   = useState('MAD'); // 'auto' | 'MAD' | 'kMAD' | 'MMAD'
  const [activeTf, setActiveTf]                     = useState(null);
  const [page, setPage]                   = useState(1);
  const [pageSize, setPageSize]           = useState(10);
  const [pageDetail, setPageDetail]       = useState(1);
  const [pageSizeDetail, setPageSizeDetail] = useState(10);
  const [sortCol, setSortCol]             = useState('');
  const [sortDir, setSortDir]             = useState('asc');

  const toggleSort = (col) => {
    setSortCol(prev => { if (prev === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortDir('asc'); } return col; });
    setPageDetail(1);
  };

  /* filtres dates (detail seulement) */
  // Réduites par défaut, sauf Notaire et Année de comptabilisation
  const [collapsedSections, setCollapsedSections] = useState(new Set([
    'filtres-cards', 'sub-tranches', 'sub-statut', 'sub-type', 'sub-statutnotaire', 'sub-tf',
  ]));
  const toggleSection = (id) => setCollapsedSections(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isOpen = (id) => !collapsedSections.has(id);

  const [dateSignClient,  setDateSignClient]  = useState(getMonthRange);
  const [dateEnCours,     setDateEnCours]     = useState(getMonthRange);
  const [dateSignNotaire, setDateSignNotaire] = useState(getMonthRange);
  const [dateMinute,      setDateMinute]      = useState(getMonthRange);
  const [dateExpedition,  setDateExpedition]  = useState(getMonthRange);
  const [dateCompta,      setDateCompta]      = useState(getYearRange);
  // Dernier filtre date activé — détermine le champ de regroupement du CA par mois
  const [lastActiveDateFilter, setLastActiveDateFilter] = useState(null);
  // Présence de la date par filtre : 'all' = Personnalisé (plage de dates, défaut) | 'with' (date renseignée) | 'without' (pas de date)
  const DEFAULT_PRESENCE = { 'sign-client': 'all', 'en-cours': 'all', 'sign-notaire': 'all', 'minute': 'all', 'expedition': 'all', 'compta': 'all' };
  const [datePresence, setDatePresence] = useState(DEFAULT_PRESENCE);
  // CA comptable : paramètres propres, indépendants des filtres du haut
  const [caCompta, setCaCompta] = useState(() => getCaComptaDefaults(new Date().getFullYear()));
  const [caComptaDetail, setCaComptaDetail] = useState(null); // { title, rows } → popup
  const closeCaComptaDetail = useCallback(() => setCaComptaDetail(null), []);

  const DATE_FIELD_LABEL = {
    'sign-client':  'signature client',
    'en-cours':     'signature en cours',
    'sign-notaire': 'signature notaire',
    'minute':       'enregistrement minute',
    'expedition':   'envoi minute notaire',
    'compta':       'comptabilisation',
  };
  const DATE_FIELD_STATE = {
    'sign-client':  dateSignClient,
    'en-cours':     dateEnCours,
    'sign-notaire': dateSignNotaire,
    'minute':       dateMinute,
    'expedition':   dateExpedition,
    'compta':       dateCompta,
  };

  useEffect(() => {
    api.get('/bien/projets')
      .then(r => setProjets(r.data))
      .catch(() => setError('Impossible de charger les projets.'));
  }, []);

  // Clé composite obligatoire dès qu'on peut avoir plusieurs projets en même temps :
  // deux projets différents peuvent avoir une tranche du même nom (ex: "Tranche 1"),
  // ce ne sont PAS les mêmes tranches — les coûts et les regroupements doivent les distinguer.
  const trancheKey = (projetId, tranche) => `${projetId}::${tranche}`;
  // Coût par type : toujours rattaché à sa tranche (projet + tranche + type). Clé JSON pour pouvoir la relire.
  const typeKey = (projetId, tranche, type) => JSON.stringify([String(projetId), tranche, type]);
  const parseCout = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;

  // Coût/m² effectif d'un bien : coût de son type dans sa tranche, sinon coût de la tranche
  const coutM2Of = useCallback((b) =>
    parseCout(coutsType[typeKey(b.projet_id, b.tranche, b.type)]) || parseCout(couts[trancheKey(b.projet_id, b.tranche)]),
  [couts, coutsType]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoutsRows = (data) => {
    const map = {}, mapType = {};
    data.forEach(row => {
      if (row.type) mapType[typeKey(row.projet_id, row.tranche, row.type)] = String(row.cout_m2);
      else          map[trancheKey(row.projet_id, row.tranche)] = String(row.cout_m2);
    });
    setCouts(map);
    setCoutsType(mapType);
  };

  const loadData = (ids) => {
    if (!ids || !ids.length) { setDetail([]); setBiens([]); setCouts({}); setCoutsType({}); return; }
    setLoading(true);
    setError('');
    setActiveStatuts(new Set());
    setActiveTypes(new Set());
    setActiveTranches(new Set());
    setActiveTranchesMois(new Set());
    setActiveStatutNotaire(new Set());
    setActiveNotaires(new Set());
    setActiveAnneesCompta(new Set());
    setActiveTf(null);
    setPage(1);
    setPageDetail(1);
    setDateSignClient(getMonthRange());
    setDateEnCours(getMonthRange());
    setDateSignNotaire(getMonthRange());
    setDateMinute(getMonthRange());
    setDateExpedition(getMonthRange());
    setDateCompta(getYearRange());
    setLastActiveDateFilter(null);
    setDatePresence(DEFAULT_PRESENCE);
    const projetIds = ids.join(',');
    api.get('/bien/tranches', { params: { projetIds } })
      .then(r => {
        setDetail(r.data.detail || []);
        setBiens(r.data.biens || []);
        // Charger les coûts sauvegardés pour ces projets
        api.get('/bien/couts', { params: { projetIds } })
          .then(rc => applyCoutsRows(rc.data))
          .catch(() => { setCouts({}); setCoutsType({}); });
      })
      .catch(() => setError('Impossible de charger les données.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(selectedProjetIds); }, [selectedProjetIds]);

  const toggleProjet = (id) => {
    setSelectedProjetIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  /* ── Résumés pour les cards (sur toute la donnée, sans filtre) ── */
  const statutSummary = useMemo(() => {
    const map = {};
    detail.forEach(r => {
      if (!map[r.statut]) map[r.statut] = 0;
      map[r.statut] += Number(r.nb_biens);
    });
    return Object.entries(map)
      .map(([statut, nb]) => ({ statut, nb }))
      .sort((a, b) => b.nb - a.nb);
  }, [detail]);

  const statutNotaireSummary = useMemo(() => {
    const map = {};
    detail.forEach(r => {
      const k = r.statut_notaire || 'NOT_YET';
      if (!map[k]) map[k] = 0;
      map[k] += Number(r.nb_biens);
    });
    return Object.keys(STATUT_NOTAIRE_CFG).map(k => ({ key: k, nb: map[k] || 0 }));
  }, [detail]);

  const trancheSummary = useMemo(() => {
    const map = {};
    detail.forEach(r => {
      const key = trancheKey(r.projet_id, r.tranche);
      if (!map[key]) map[key] = { key, projet_id: r.projet_id, projet_nom: r.projet_nom, tranche: r.tranche, nb: 0 };
      map[key].nb += Number(r.nb_biens);
    });
    return Object.values(map).sort((a, b) =>
      (a.projet_nom || '').localeCompare(b.projet_nom || '', 'fr') || a.tranche.localeCompare(b.tranche, 'fr')
    );
  }, [detail]);

  const typeSummary = useMemo(() => {
    const map = {};
    detail.forEach(r => {
      if (!map[r.type]) map[r.type] = 0;
      map[r.type] += Number(r.nb_biens);
    });
    return Object.entries(map).map(([type, nb]) => ({ type, nb })).sort((a, b) => b.nb - a.nb);
  }, [detail]);

  const tfSummary = useMemo(() => {
    let titres = 0, non_titres = 0;
    detail.forEach(r => {
      if (Number(r.has_tf) === 1) titres      += Number(r.nb_biens);
      else                        non_titres  += Number(r.nb_biens);
    });
    return { titres, non_titres };
  }, [detail]);

  // Pas de notaire dans `detail` (agrégat backend) → calculé depuis `biens` (non filtré)
  const notaireSummary = useMemo(() => {
    const map = {};
    biens.forEach(r => {
      const k = r.notaire || 'Sans notaire';
      if (!map[k]) map[k] = 0;
      map[k] += 1;
    });
    return Object.entries(map).map(([notaire, nb]) => ({ notaire, nb })).sort((a, b) => b.nb - a.nb);
  }, [biens]);

  // Idem pour les années de comptabilisation (extraites de contrat.dateCompta)
  const anneeComptaSummary = useMemo(() => {
    const map = {};
    biens.forEach(r => {
      const k = r.date_compta ? new Date(r.date_compta).getFullYear() : 'Sans date';
      if (!map[k]) map[k] = 0;
      map[k] += 1;
    });
    return Object.entries(map)
      .map(([annee, nb]) => ({ annee, nb }))
      .sort((a, b) => b.annee.localeCompare(a.annee, 'fr', { numeric: true }));
  }, [biens]);

  /* ── Échelle d'affichage des montants (Auto / MAD / kMAD / MMAD) ── */
  const scaleMoney = (val) => {
    const v = Number(val) || 0;
    if (unitScale === 'auto') {
      if (Math.abs(v) >= 1000000) return { value: v / 1000000, suffix: ' M' };
      if (Math.abs(v) >= 1000)    return { value: v / 1000,    suffix: ' k' };
      return { value: v, suffix: '' };
    }
    if (unitScale === 'kMAD') return { value: v / 1000,    suffix: ' k' };
    if (unitScale === 'MMAD') return { value: v / 1000000, suffix: ' M' };
    return { value: v, suffix: '' };
  };

  const fmtMoney = (val) => {
    if (val === '' || val == null) return '—';
    const { value, suffix } = scaleMoney(val);
    return `${fmtN(value)}${suffix}`;
  };

  const resetPages = () => { setPage(1); setPageDetail(1); };

  const toggleStatut = useCallback((statut) => {
    setActiveStatuts(prev => { const n = new Set(prev); n.has(statut) ? n.delete(statut) : n.add(statut); return n; });
    resetPages();
  }, []);

  const toggleType = useCallback((type) => {
    setActiveTypes(prev => { const n = new Set(prev); n.has(type) ? n.delete(type) : n.add(type); return n; });
    resetPages();
  }, []);

  const toggleTranche = useCallback((tranche) => {
    setActiveTranches(prev => { const n = new Set(prev); n.has(tranche) ? n.delete(tranche) : n.add(tranche); return n; });
    resetPages();
  }, []);

  const toggleTrancheMois = useCallback((tranche) => {
    setActiveTranchesMois(prev => { const n = new Set(prev); n.has(tranche) ? n.delete(tranche) : n.add(tranche); return n; });
  }, []);

  const toggleStatutNotaire = useCallback((key) => {
    setActiveStatutNotaire(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    resetPages();
  }, []);

  const toggleNotaire = useCallback((notaire) => {
    setActiveNotaires(prev => { const n = new Set(prev); n.has(notaire) ? n.delete(notaire) : n.add(notaire); return n; });
    resetPages();
  }, []);

  const toggleAnneeCompta = useCallback((annee) => {
    setActiveAnneesCompta(prev => { const n = new Set(prev); n.has(annee) ? n.delete(annee) : n.add(annee); return n; });
    resetPages();
  }, []);

  const toggleTf = useCallback((key) => {
    setActiveTf(prev => prev === key ? null : key);
    resetPages();
  }, []);

  const handleCout = (projetId, tranche, val) =>
    setCouts(prev => ({ ...prev, [trancheKey(projetId, tranche)]: val }));

  const saveCout = (projetId, tranche, val) => {
    const cout_m2 = parseFloat(String(val).replace(',', '.'));
    if (!projetId) return;
    api.post('/bien/couts', { projetId, tranche, cout_m2: isNaN(cout_m2) ? 0 : cout_m2 })
      .catch(() => {});
  };

  const handleCoutType = (projetId, tranche, type, val) =>
    setCoutsType(prev => ({ ...prev, [typeKey(projetId, tranche, type)]: val }));

  // Valeur vide → le backend supprime la surcharge, le type reprend le coût de la tranche
  const saveCoutType = (projetId, tranche, type, val) => {
    if (!projetId) return;
    const cout_m2 = parseFloat(String(val).replace(',', '.'));
    api.post('/bien/couts', { projetId, tranche, type, cout_m2: isNaN(cout_m2) ? '' : cout_m2 })
      .catch(() => {});
  };

  const saveAllCouts = async () => {
    if (!selectedProjetIds.length || savingAll) return;
    setSavingAll(true);
    setSaveMsg('');
    try {
      await Promise.all(
        Object.entries(couts).map(([key, val]) => {
          const sep = key.indexOf('::');
          const projetId = key.slice(0, sep);
          const tranche = key.slice(sep + 2);
          const cout_m2 = parseFloat(String(val).replace(',', '.'));
          return api.post('/bien/couts', { projetId, tranche, cout_m2: isNaN(cout_m2) ? 0 : cout_m2 });
        }).concat(Object.entries(coutsType).map(([key, val]) => {
          const [projetId, tranche, type] = JSON.parse(key);
          const cout_m2 = parseFloat(String(val).replace(',', '.'));
          return api.post('/bien/couts', { projetId, tranche, type, cout_m2: isNaN(cout_m2) ? '' : cout_m2 });
        }))
      );
      setSaveMsg('ok');
    } catch {
      setSaveMsg('err');
    } finally {
      setSavingAll(false);
      setTimeout(() => setSaveMsg(''), 3000);
    }
  };

  const reloadCouts = () => {
    if (!selectedProjetIds.length) return;
    api.get('/bien/couts', { params: { projetIds: selectedProjetIds.join(',') } })
      .then(rc => {
        applyCoutsRows(rc.data);
        setSaveMsg('reload');
        setTimeout(() => setSaveMsg(''), 2500);
      })
      .catch(() => {});
  };

  const exportExcel = () => {
    const projetNames = projets.filter(p => selectedProjetIds.includes(p.id)).map(p => p.name);
    const projetName = projetNames.length === 1 ? projetNames[0] : (projetNames.length > 1 ? 'multi_projets' : 'projet');
    const rows = filteredBiens.map(b => {
      const coutM2  = coutM2Of(b) || null;
      const coutTot = coutM2 != null ? (Number(b.superficie) || 0) * coutM2 : null;
      const caBien  = Number(b.ca) || 0;
      const marge   = coutTot != null ? caBien - coutTot : null;
      const margePct = (marge != null && caBien > 0) ? Number(((marge / caBien) * 100).toFixed(1)) : null;
      return {
        'ID Bien':         b.id,
        'N°':              b.numero || '',
        ...(isMultiProjet ? { 'Projet': b.projet_nom || '' } : {}),
        'ID Vente':        b.contrat_id || '',
        'Tranche':         b.tranche,
        'Statut':          b.statut,
        'Statut notaire':  (STATUT_NOTAIRE_CFG[b.statut_notaire || 'NOT_YET'] || STATUT_NOTAIRE_CFG.NOT_YET).label,
        'Désignation':     b.designation || '',
        'Type':            b.type || '',
        'Superficie (m²)': b.superficie != null ? Number(b.superficie) : '',
        'Prix/m² (DB)':    b.prix_m    != null ? Number(b.prix_m)    : '',
        'Prix vente':      b.prix_vente != null ? Number(b.prix_vente) : '',
        'Bien Prix/m²':    b.bien_prix_m    != null ? Number(b.bien_prix_m)    : '',
        'Bien Prix vente': b.bien_prix_vente != null ? Number(b.bien_prix_vente) : '',
        'CA':              caBien || '',
        'Signé client':            b.date_signature_client  ? fmtDate(b.date_signature_client)  : '',
        'Signature en cours':      b.date_en_cours          ? fmtDate(b.date_en_cours)           : '',
        'Signé notaire':           b.date_signature_notaire ? fmtDate(b.date_signature_notaire)  : '',
        'Enreg. minute':           b.date_minute            ? fmtDate(b.date_minute)             : '',
        'Envoi minute notaire':    b.date_expedition        ? fmtDate(b.date_expedition)         : '',
        'Date Compta':     b.date_compta ? fmtDate(b.date_compta) : '',
        'Année Compta':    b.date_compta ? new Date(b.date_compta).getFullYear() : '',
        'Notaire':         b.notaire || '',
        'Coût/m² saisi':   coutM2  ?? '',
        'Coût total':      coutTot ?? '',
        'Marge':           marge ?? '',
        '%':               margePct ?? '',
      };
    });
    rows.push({
      'ID Bien': 'TOTAL',
      'N°': '',
      ...(isMultiProjet ? { 'Projet': '' } : {}),
      'ID Vente': '',
      'Tranche': '',
      'Statut': '',
      'Désignation': '',
      'Type': '',
      'Superficie (m²)': totalSuperficieBiens || '',
      'Prix/m² (DB)': '',
      'Prix vente': totalPrixVenteBiens || '',
      'CA': totalCaBiens || '',
      'Signé client': '',
      'Signature en cours': '',
      'Signé notaire': '',
      'Enreg. minute': '',
      'Envoi minute notaire': '',
      'Date Compta': '',
      'Année Compta': '',
      'Notaire': '',
      'Coût/m² saisi': '',
      'Coût total': totalCoutTotalBiens || '',
      'Marge': totalMargeBiens || '',
      '%': totalMargePctBiens != null ? Number(totalMargePctBiens.toFixed(1)) : '',
    });
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Biens');
    XLSX.writeFile(wb, `tranches_${projetName.replace(/\s+/g, '_')}.xlsx`);
  };

  const exportExcelCouts = () => {
    const projetNames = projets.filter(p => selectedProjetIds.includes(p.id)).map(p => p.name);
    const projetName = projetNames.length === 1 ? projetNames[0] : (projetNames.length > 1 ? 'multi_projets' : 'projet');
    const rows = filteredTranches.map(l => {
      const coutVal = parseFloat((couts[trancheKey(l.projet_id, l.tranche)] || '').replace(',', '.')) || 0;
      const montant = l.cout_total;
      return {
        'Tranche': l.tranche,
        ...(isMultiProjet ? { 'Projet': l.projet_nom || '' } : {}),
        'Nb biens': l.nb_biens,
        'Superficie (m²)': l.superficie_totale,
        'Coût/m²': coutVal || '',
        'Montant estimé': montant || '',
      };
    });
    rows.push({
      'Tranche': 'TOTAL',
      ...(isMultiProjet ? { 'Projet': '' } : {}),
      'Nb biens': totalBiens || '',
      'Superficie (m²)': totalSuperficie || '',
      'Coût/m²': '',
      'Montant estimé': totalMontant || '',
    });
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CoutTranche');
    XLSX.writeFile(wb, `cout_par_tranche_${projetName.replace(/\s+/g, '_')}.xlsx`);
  };

  const exportExcelCA = () => {
    const projetNames = projets.filter(p => selectedProjetIds.includes(p.id)).map(p => p.name);
    const projetName = projetNames.length === 1 ? projetNames[0] : (projetNames.length > 1 ? 'multi_projets' : 'projet');
    const rows = filteredTranches.map(l => {
      const coutVal  = parseFloat((couts[trancheKey(l.projet_id, l.tranche)] || '').replace(',', '.')) || 0;
      const montant  = l.cout_total;
      const marge    = l.ca_totale - montant;
      const margePct = l.ca_totale > 0 ? (marge / l.ca_totale) * 100 : null;
      const hasCout  = coutVal > 0 || l.cout_total > 0;
      return {
        'Tranche': l.tranche,
        ...(isMultiProjet ? { 'Projet': l.projet_nom || '' } : {}),
        'Nb biens': l.nb_biens,
        'Superficie (m²)': l.superficie_totale,
        'Coût/m²': coutVal || '',
        'Coût total': hasCout ? montant : '',
        'CA': l.ca_totale || '',
        'Marge': (hasCout && l.ca_totale > 0) ? marge : '',
        '%': (hasCout && margePct != null) ? Number(margePct.toFixed(1)) : '',
      };
    });
    rows.push({
      'Tranche': 'TOTAL',
      ...(isMultiProjet ? { 'Projet': '' } : {}),
      'Nb biens': totalBiens || '',
      'Superficie (m²)': totalSuperficie || '',
      'Coût/m²': '',
      'Coût total': totalMontant || '',
      'CA': totalCA || '',
      'Marge': totalMarge || '',
      '%': totalMargePct != null ? Number(totalMargePct.toFixed(1)) : '',
    });
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CA_Tranche');
    XLSX.writeFile(wb, `ca_par_tranche_${projetName.replace(/\s+/g, '_')}.xlsx`);
  };

  const exportExcelCAMois = () => {
    const projetNames = projets.filter(p => selectedProjetIds.includes(p.id)).map(p => p.name);
    const projetName = projetNames.length === 1 ? projetNames[0] : (projetNames.length > 1 ? 'multi_projets' : 'projet');
    const rows = caParMois.map(l => {
      const marge     = l.ca_totale - l.montant_estime;
      const margePct  = l.ca_totale > 0 ? (marge / l.ca_totale) * 100 : null;
      const coutMoyen = l.superficie_totale > 0 ? l.montant_estime / l.superficie_totale : 0;
      return {
        'Mois': monthLabel(l.mois),
        ...(isMultiProjet ? { 'Projet': l.projet_nom || '' } : {}),
        'Nb biens': l.nb_biens,
        'Superficie (m²)': l.superficie_totale,
        'Coût/m² moyen': coutMoyen || '',
        'Coût total': l.montant_estime || '',
        'CA': l.ca_totale || '',
        'Marge': (l.montant_estime > 0 && l.ca_totale > 0) ? marge : '',
        '%': (l.montant_estime > 0 && margePct != null) ? Number(margePct.toFixed(1)) : '',
      };
    });
    rows.push({
      'Mois': 'TOTAL',
      ...(isMultiProjet ? { 'Projet': '' } : {}),
      'Nb biens': totalBiensMois || '',
      'Superficie (m²)': totalSuperficieMois || '',
      'Coût/m² moyen': '',
      'Coût total': totalMontantMois || '',
      'CA': totalCAMois || '',
      'Marge': totalMargeMois || '',
      '%': totalMargePctMois != null ? Number(totalMargePctMois.toFixed(1)) : '',
    });
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CA_Mois');
    XLSX.writeFile(wb, `ca_par_mois_${projetName.replace(/\s+/g, '_')}.xlsx`);
  };

  const inDateRange = (val, { debut, fin }) => {
    if (!debut && !fin) return true;
    if (!val) return false;
    const d = new Date(val);
    if (debut && d < new Date(debut)) return false;
    if (fin   && d > new Date(fin + 'T23:59:59')) return false;
    return true;
  };

  const filteredBiens = useMemo(() => {
    let rows = biens;
    if (activeStatuts.size > 0)         rows = rows.filter(r => activeStatuts.has(r.statut));
    if (activeTypes.size > 0)           rows = rows.filter(r => activeTypes.has(r.type));
    if (activeTranches.size > 0)        rows = rows.filter(r => activeTranches.has(trancheKey(r.projet_id, r.tranche)));
    if (activeStatutNotaire.size > 0)   rows = rows.filter(r => activeStatutNotaire.has(r.statut_notaire || 'NOT_YET'));
    if (activeNotaires.size > 0)        rows = rows.filter(r => activeNotaires.has(r.notaire || 'Sans notaire'));
    if (activeAnneesCompta.size > 0)    rows = rows.filter(r => activeAnneesCompta.has(r.date_compta ? String(new Date(r.date_compta).getFullYear()) : 'Sans date'));
    if (activeTf === 'titres')          rows = rows.filter(r => Number(r.has_tf) === 1);
    if (activeTf === 'non_titres')      rows = rows.filter(r => Number(r.has_tf) === 0);
    if (dateSignClient.active  && datePresence['sign-client'] === 'all' && (dateSignClient.debut  || dateSignClient.fin))  rows = rows.filter(r => inDateRange(r.date_signature_client,  dateSignClient));
    if (dateEnCours.active     && datePresence['en-cours'] === 'all' && (dateEnCours.debut     || dateEnCours.fin))     rows = rows.filter(r => inDateRange(r.date_en_cours,          dateEnCours));
    if (dateSignNotaire.active && datePresence['sign-notaire'] === 'all' && (dateSignNotaire.debut || dateSignNotaire.fin)) rows = rows.filter(r => inDateRange(r.date_signature_notaire, dateSignNotaire));
    if (dateMinute.active      && datePresence['minute'] === 'all' && (dateMinute.debut      || dateMinute.fin))      rows = rows.filter(r => inDateRange(r.date_minute,            dateMinute));
    if (dateExpedition.active  && datePresence['expedition'] === 'all' && (dateExpedition.debut  || dateExpedition.fin))  rows = rows.filter(r => inDateRange(r.date_expedition,        dateExpedition));
    if (dateCompta.active      && datePresence['compta'] === 'all' && (dateCompta.debut || dateCompta.fin)) rows = rows.filter(r => inDateRange(r.date_compta, dateCompta));
    const dateActive = {
      'sign-client': dateSignClient.active, 'en-cours': dateEnCours.active, 'sign-notaire': dateSignNotaire.active,
      'minute': dateMinute.active, 'expedition': dateExpedition.active, 'compta': dateCompta.active,
    };
    Object.entries(datePresence).forEach(([id, p]) => {
      if (p === 'all' || !dateActive[id]) return;
      const field = DATE_FIELD_MAP[id];
      rows = rows.filter(r => (p === 'with') === !!r[field]);
    });
    return rows;
  }, [biens, activeStatuts, activeTypes, activeTranches, activeStatutNotaire, activeNotaires, activeAnneesCompta, activeTf, dateSignClient, dateEnCours, dateSignNotaire, dateMinute, dateExpedition, dateCompta, datePresence]);

  /* ── Tableau tranches agrégé depuis filteredBiens (tous filtres inclus) ── */
  const filteredTranches = useMemo(() => {
    const map = {};
    filteredBiens.forEach(r => {
      const key = trancheKey(r.projet_id, r.tranche);
      if (!map[key]) map[key] = { key, projet_id: r.projet_id, projet_nom: r.projet_nom, tranche: r.tranche, nb_biens: 0, superficie_totale: 0, ca_totale: 0, cout_total: 0, types: {} };
      map[key].nb_biens       += 1;
      map[key].superficie_totale += Number(r.superficie) || 0;
      map[key].ca_totale         += Number(r.ca) || 0;
      // Coût réel = somme des coûts des biens (coût du type si saisi, sinon coût de la tranche)
      map[key].cout_total        += (Number(r.superficie) || 0) * coutM2Of(r);
      const t = map[key].types[r.type] || (map[key].types[r.type] = { type: r.type, nb_biens: 0, superficie_totale: 0, cout_total: 0, ca_totale: 0 });
      t.nb_biens += 1;
      t.superficie_totale += Number(r.superficie) || 0;
      t.cout_total += (Number(r.superficie) || 0) * coutM2Of(r);
      t.ca_totale  += Number(r.ca) || 0;
    });
    return Object.values(map).sort((a, b) =>
      (a.projet_nom || '').localeCompare(b.projet_nom || '', 'fr') || a.tranche.localeCompare(b.tranche, 'fr')
    );
  }, [filteredBiens, coutM2Of]);

  const totalBiens      = filteredTranches.reduce((s, l) => s + l.nb_biens, 0);
  const totalSuperficie = filteredTranches.reduce((s, l) => s + l.superficie_totale, 0);
  const totalCA         = filteredTranches.reduce((s, l) => s + l.ca_totale, 0);
  const totalMontant    = filteredTranches.reduce((s, l) => s + l.cout_total, 0);
  const totalMarge = totalCA - totalMontant;
  const totalMargePct = totalCA > 0 ? (totalMarge / totalCA) * 100 : null;

  /* ── CA par mois de signature notaire, filtrable par tranche (cards dédiées) ── */
  const monthKey = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d)) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const monthLabel = (key) => {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const biensForMois = useMemo(() => {
    if (activeTranchesMois.size === 0) return filteredBiens;
    return filteredBiens.filter(r => activeTranchesMois.has(trancheKey(r.projet_id, r.tranche)));
  }, [filteredBiens, activeTranchesMois]);

  // Champ date utilisé pour le regroupement mensuel : suit le filtre date actif en haut de page
  // (le dernier coché s'il y en a plusieurs), sinon "signature notaire" par défaut.
  const activeDateFilterIds = Object.keys(DATE_FIELD_MAP).filter(id => DATE_FIELD_STATE[id].active);
  const moisFieldId = activeDateFilterIds.length === 0
    ? 'sign-notaire'
    : (lastActiveDateFilter && activeDateFilterIds.includes(lastActiveDateFilter))
      ? lastActiveDateFilter
      : activeDateFilterIds[activeDateFilterIds.length - 1];
  const moisField = DATE_FIELD_MAP[moisFieldId];

  // Enrichit un bien avec son coût (superficie × coût/m² du type ou de la tranche) et sa marge — pour popups / exports
  const enrichBien = useCallback((b) => {
    const coutM2  = coutM2Of(b);
    const ca      = Number(b.ca) || 0;
    const coutTot = coutM2 > 0 ? (Number(b.superficie) || 0) * coutM2 : null;
    const marge   = coutTot != null ? ca - coutTot : null;
    return { ...b, ca_num: ca, cout_m2: coutM2 > 0 ? coutM2 : null, cout_total: coutTot, marge, marge_pct: marge != null && ca > 0 ? (marge / ca) * 100 : null };
  }, [coutM2Of]);

  const caParMois = useMemo(() => {
    const map = {};
    biensForMois.forEach(r => {
      const mois = monthKey(r[moisField]);
      if (!mois) return;
      const key = `${mois}::${r.projet_id}`;
      const coutVal = coutM2Of(r);
      if (!map[key]) map[key] = { key, mois, projet_id: r.projet_id, projet_nom: r.projet_nom, nb_biens: 0, superficie_totale: 0, montant_estime: 0, ca_totale: 0, list: [] };
      map[key].nb_biens          += 1;
      map[key].superficie_totale += Number(r.superficie) || 0;
      map[key].montant_estime    += (Number(r.superficie) || 0) * coutVal;
      map[key].ca_totale         += Number(r.ca) || 0;
      map[key].list.push(enrichBien(r));
    });
    return Object.values(map).sort((a, b) =>
      a.mois.localeCompare(b.mois) || (a.projet_nom || '').localeCompare(b.projet_nom || '', 'fr')
    );
  }, [biensForMois, coutM2Of, enrichBien, moisField]);

  const totalBiensMois      = caParMois.reduce((s, l) => s + l.nb_biens, 0);
  const totalSuperficieMois = caParMois.reduce((s, l) => s + l.superficie_totale, 0);
  const totalMontantMois    = caParMois.reduce((s, l) => s + l.montant_estime, 0);
  const totalCAMois         = caParMois.reduce((s, l) => s + l.ca_totale, 0);
  const totalMargeMois      = totalCAMois - totalMontantMois;
  const totalMargePctMois   = totalCAMois > 0 ? (totalMargeMois / totalCAMois) * 100 : null;

  /* ── CA comptable : signés notaire N / N-1 × comptabilisés dans l'année / sans date compta ── */
  const caComptaRows = useMemo(() => {
    const emptyGroup = () => ({ nb: 0, ca: 0, cout: 0, list: [] });
    const addTo = (g, b) => { g.nb += 1; g.ca += b.ca_num; g.cout += b.cout_total || 0; g.list.push(b); };
    const build = (key, label, debut, fin) => {
      const row = { key, label, debut, fin, compta: emptyGroup(), non: emptyGroup(), total: emptyGroup(), autre: emptyGroup(), sansCout: [] };
      biens.forEach(raw => {
        if (!raw.date_signature_notaire || !inDateRange(raw.date_signature_notaire, { debut, fin })) return;
        const b = enrichBien(raw);
        if (raw.date_compta && new Date(raw.date_compta).getFullYear() !== Number(caCompta.annee)) { addTo(row.autre, b); return; }
        addTo(raw.date_compta ? row.compta : row.non, b);
        addTo(row.total, b);
        if (b.cout_m2 == null) row.sansCout.push(b);
      });
      return row;
    };
    return [
      build('n',  'N',   caCompta.nDebut,  caCompta.nFin),
      build('n1', 'N-1', caCompta.n1Debut, caCompta.n1Fin),
    ];
  }, [biens, caCompta, enrichBien]);

  const caComptaTotal = useMemo(() => {
    const merge = (k) => caComptaRows.reduce((g, r) => ({
      nb: g.nb + r[k].nb, ca: g.ca + r[k].ca, cout: g.cout + r[k].cout, list: [...g.list, ...r[k].list],
    }), { nb: 0, ca: 0, cout: 0, list: [] });
    return {
      key: 'total', label: 'TOTAL', debut: '', fin: '',
      compta: merge('compta'), non: merge('non'), total: merge('total'), autre: merge('autre'),
      sansCout: caComptaRows.flatMap(r => r.sansCout),
    };
  }, [caComptaRows]);

  const CC_GROUPS = [
    { key: 'compta', label: `Comptabilisé ${caCompta.annee}`, title: `comptabilisés ${caCompta.annee}`, color: C.primary, bg: '#edf7ed', border: '#c8dfc8' },
    { key: 'non',    label: 'Non comptabilisé',               title: 'non comptabilisés',               color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
    { key: 'total',  label: 'Total',                          title: 'total',                           color: '#3d3d3d', bg: '#eceae6', border: '#d4c8bc', pct: true },
  ];

  // Nombre cliquable → ouvre le popup détail des biens
  const ClickNum = ({ list, title, prefix = 'CA comptable', children }) => (
    list.length > 0 ? (
      <span
        onClick={() => setCaComptaDetail({ title: `${prefix} — ${title}`, rows: list })}
        title="Voir le détail"
        style={{ cursor: 'pointer', borderBottom: '1px dotted currentColor' }}
      >
        {children}
      </span>
    ) : children
  );

  const exportExcelCaCompta = () => {
    const projetNames = projets.filter(p => selectedProjetIds.includes(p.id)).map(p => p.name);
    const projetName = projetNames.length === 1 ? projetNames[0] : (projetNames.length > 1 ? 'multi_projets' : 'projet');
    const rows = [...caComptaRows, caComptaTotal].map(r => {
      const out = { 'Signé notaire': r.label, 'Du': r.debut ? fmtDate(r.debut) : '', 'Au': r.fin ? fmtDate(r.fin) : '' };
      CC_GROUPS.forEach(g => {
        const v = r[g.key];
        out[`${g.label} · Nb`]    = v.nb;
        out[`${g.label} · CA`]    = v.ca;
        out[`${g.label} · Coût`]  = v.cout;
        out[`${g.label} · Marge`] = v.ca - v.cout;
        if (g.pct) out[`${g.label} · %`] = v.ca > 0 ? Number((((v.ca - v.cout) / v.ca) * 100).toFixed(1)) : '';
      });
      out['Biens sans coût/m²'] = r.sansCout.length;
      return out;
    });
    // Feuille 2 : détail de tous les biens avec leur statut compta
    const statuts = [
      ['compta', `Comptabilisé ${caCompta.annee}`],
      ['non',    'Non comptabilisé'],
      ['autre',  'Comptabilisé autre année (exclu)'],
    ];
    const detailRows = caComptaRows.flatMap(r => statuts.flatMap(([gKey, statutLbl]) => r[gKey].list.map(b => ({
      'Période':         r.label,
      'Statut compta':   statutLbl,
      'ID Bien':         b.id,
      'N°':              b.numero || '',
      ...(isMultiProjet ? { 'Projet': b.projet_nom || '' } : {}),
      'ID Vente':        b.contrat_id || '',
      'Tranche':         b.tranche,
      'Désignation':     b.designation || '',
      'Type':            b.type || '',
      'Statut bien':     b.statut || '',
      'Superficie (m²)': b.superficie != null ? Number(b.superficie) : '',
      'CA':              b.ca_num,
      'Coût/m²':         b.cout_m2 ?? '',
      'Coût total':      b.cout_total ?? '',
      'Marge':           b.marge ?? '',
      '%':               b.marge_pct != null ? Number(b.marge_pct.toFixed(1)) : '',
      'Signé notaire':   fmtDate(b.date_signature_notaire),
      'Date compta':     b.date_compta ? fmtDate(b.date_compta) : '',
      'Notaire':         b.notaire || '',
    }))));
    const ws = styleExcelHeader(XLSX.utils.json_to_sheet(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CA_Comptable');
    XLSX.utils.book_append_sheet(wb, styleExcelHeader(XLSX.utils.json_to_sheet(detailRows)), 'Détail');
    XLSX.writeFile(wb, `ca_comptable_${caCompta.annee}_${projetName.replace(/\s+/g, '_')}.xlsx`);
  };

  const sortedBiens = useMemo(() => {
    if (!sortCol) return filteredBiens;
    return [...filteredBiens].sort((a, b) => {
      let av, bv;
      if (sortCol === 'cout_m2') {
        av = coutM2Of(a);
        bv = coutM2Of(b);
      } else if (sortCol === 'cout_total') {
        av = (Number(a.superficie) || 0) * (coutM2Of(a));
        bv = (Number(b.superficie) || 0) * (coutM2Of(b));
      } else if (sortCol === 'marge' || sortCol === 'margepct') {
        const coutA = (Number(a.superficie) || 0) * (coutM2Of(a));
        const coutB = (Number(b.superficie) || 0) * (coutM2Of(b));
        const margeA = (Number(a.ca) || 0) - coutA;
        const margeB = (Number(b.ca) || 0) - coutB;
        if (sortCol === 'marge') { av = margeA; bv = margeB; }
        else { av = Number(a.ca) > 0 ? (margeA / Number(a.ca)) * 100 : null; bv = Number(b.ca) > 0 ? (margeB / Number(b.ca)) * 100 : null; }
      } else {
        av = a[sortCol];
        bv = b[sortCol];
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const na = Number(av), nb = Number(bv);
      if (!isNaN(na) && !isNaN(nb) && av !== '' && bv !== '') return sortDir === 'asc' ? na - nb : nb - na;
      const sa = String(av), sb = String(bv);
      return sortDir === 'asc' ? sa.localeCompare(sb, 'fr') : sb.localeCompare(sa, 'fr');
    });
  }, [filteredBiens, sortCol, sortDir, coutM2Of]);

  const totalSuperficieBiens = filteredBiens.reduce((s, b) => s + (Number(b.superficie) || 0), 0);
  const totalPrixVenteBiens  = filteredBiens.reduce((s, b) => s + (Number(b.prix_vente) || 0), 0);
  const totalCaBiens         = filteredBiens.reduce((s, b) => s + (Number(b.ca) || 0), 0);
  const totalCoutTotalBiens  = filteredBiens.reduce((s, b) => {
    const coutM2 = coutM2Of(b);
    return s + (Number(b.superficie) || 0) * coutM2;
  }, 0);
  const totalMargeBiens    = totalCaBiens - totalCoutTotalBiens;
  const totalMargePctBiens = totalCaBiens > 0 ? (totalMargeBiens / totalCaBiens) * 100 : null;

  const totalPagesDetail  = Math.max(1, Math.ceil(filteredBiens.length / pageSizeDetail));
  const pagedBiens        = sortedBiens.slice((pageDetail - 1) * pageSizeDetail, pageDetail * pageSizeDetail);

  const hasDateFilter = (dateSignClient.active  && (dateSignClient.debut  || dateSignClient.fin))
                     || (dateEnCours.active     && (dateEnCours.debut     || dateEnCours.fin))
                     || (dateSignNotaire.active && (dateSignNotaire.debut || dateSignNotaire.fin))
                     || (dateMinute.active      && (dateMinute.debut      || dateMinute.fin))
                     || (dateExpedition.active  && (dateExpedition.debut  || dateExpedition.fin))
                     || (dateCompta.active      && (dateCompta.debut      || dateCompta.fin))
                     || Object.entries(datePresence).some(([id, p]) => p !== 'all' && DATE_FIELD_STATE[id].active);
  const hasFilter     = activeStatuts.size > 0 || activeTypes.size > 0 || activeTranches.size > 0 || activeStatutNotaire.size > 0 || activeNotaires.size > 0 || activeAnneesCompta.size > 0 || activeTf !== null || hasDateFilter;
  const totalPages    = Math.max(1, Math.ceil(filteredTranches.length / pageSize));
  const pagedTranches = filteredTranches.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div style={{ minHeight: '100vh', background: C.light, padding: '0 0 40px' }}>
      <div style={{ padding: '16px 12px', maxWidth: 1100, margin: '0 auto' }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <img src="/iconshems.png" alt="Shems" style={{ height: 40, objectFit: 'contain', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: C.primary, lineHeight: 1.2 }}>Coût par tranche</div>
            <div style={{ fontSize: '0.73rem', color: '#888', marginTop: 2 }}>Analyse superficie · Coût/m² · Statuts commerciaux</div>
          </div>

          <UnitToggle value={unitScale} onChange={setUnitScale} />
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {/* ── Sélecteur projets (multi-sélection par cards) ─────────── */}
        <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 18px', marginBottom: 14, boxShadow: '0 1px 4px rgba(109,78,54,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <label style={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
              PROJETS
              {selectedProjetIds.length > 0 && (
                <span style={{ marginLeft: 6, color: C.primary }}>
                  · {selectedProjetIds.length} sélectionné{selectedProjetIds.length > 1 ? 's' : ''}
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedProjetIds.length > 0 && (
                <button
                  onClick={() => loadData(selectedProjetIds)}
                  disabled={loading}
                  style={{ height: 34, padding: '0 14px', borderRadius: 7, cursor: loading ? 'not-allowed' : 'pointer', background: C.primary, color: '#fff', border: 'none', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: loading ? 0.8 : 1 }}
                >
                  <FaSync size={11} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                  Actualiser
                </button>
              )}
              {hasFilter && (
                <button
                  onClick={() => { setActiveStatuts(new Set()); setActiveTypes(new Set()); setActiveTranches(new Set()); setActiveStatutNotaire(new Set()); setActiveNotaires(new Set()); setActiveAnneesCompta(new Set()); setActiveTf(null); setDateSignClient(getMonthRange()); setDateEnCours(getMonthRange()); setDateSignNotaire(getMonthRange()); setDateMinute(getMonthRange()); setDateExpedition(getMonthRange()); setDateCompta(getYearRange()); setLastActiveDateFilter(null); setDatePresence(DEFAULT_PRESENCE); }}
                  style={{ height: 34, padding: '0 14px', borderRadius: 7, cursor: 'pointer', background: '#fff', color: '#c62828', border: '1.5px solid #c62828', fontSize: '0.78rem', fontWeight: 600 }}
                >
                  ✕ Réinitialiser filtres
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {projets.map((p, i) => {
              const active = selectedProjetIds.includes(p.id);
              const color = typeColor(i);
              return (
                <button
                  key={p.id}
                  onClick={() => toggleProjet(p.id)}
                  style={{
                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${active ? color : C.border}`,
                    background: active ? color : '#fff',
                    color: active ? '#fff' : '#4a505c',
                    fontSize: '0.78rem', fontWeight: 700,
                    boxShadow: active ? `0 0 0 2px ${color}33` : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {p.name}
                </button>
              );
            })}
            {projets.length > 1 && (
              <button
                onClick={() => setSelectedProjetIds(selectedProjetIds.length === projets.length ? [] : projets.map(p => p.id))}
                style={{ padding: '8px 14px', borderRadius: 8, cursor: 'pointer', border: `1.5px dashed ${C.border}`, background: '#fafafa', color: '#888', fontSize: '0.72rem', fontWeight: 700 }}
              >
                {selectedProjetIds.length === projets.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            )}
          </div>
        </div>

        {/* ── Cartes filtres dates (repliables, affichées par défaut) ── */}
        {selectedProjetIds.length > 0 && (
          <div style={{ marginBottom: 20, border: '1.5px solid #e4dfd8', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <SectionHeader
            label="Filtres dates"
            open={isOpen('filtres-dates')}
            onToggle={() => toggleSection('filtres-dates')}
            selectedCount={Object.values(DATE_FIELD_STATE).filter(st => st.active).length}
          />
          {isOpen('filtres-dates') && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: 12, background: '#fff' }}>
            {[
              { id: 'sign-client',  label: 'Signé client',             state: dateSignClient,  set: setDateSignClient,  color: '#546e7a', icon: '✍️', presence: ['Signé', 'Non signé'] },
              { id: 'en-cours',     label: 'Signature en cours',        state: dateEnCours,     set: setDateEnCours,     color: '#e65100', icon: '⏳', presence: ['En cours', 'Pas en cours'] },
              { id: 'sign-notaire', label: 'Signé notaire',             state: dateSignNotaire, set: setDateSignNotaire, color: '#2e7d32', icon: '🏛️', presence: ['Signé', 'Non signé'] },
              { id: 'minute',       label: 'Enreg. minute',             state: dateMinute,      set: setDateMinute,      color: '#6a1b9a', icon: '📋', presence: ['Enregistré', 'Non enregistré'] },
              { id: 'expedition',   label: 'Envoi minute notaire',      state: dateExpedition,  set: setDateExpedition,  color: '#ad1457', icon: '📨', presence: ['Envoyé', 'Non envoyé'] },
              { id: 'compta',       label: 'Date Compta',               state: dateCompta,      set: setDateCompta,      color: '#6d4e36', icon: '🧾', presence: ['Comptabilisé', 'Non comptabilisé'] },
            ].map(({ id, label, state, set, color, icon, presence }) => {
              // Signé / Non signé = toutes dates confondues → la plage de dates est ignorée
              const noDate = presence && datePresence[id] !== 'all';
              return (
              <div key={id} style={{
                flex: '1 1 220px', minWidth: 200,
                background: state.active ? `${color}08` : '#fff',
                border: `1.5px solid ${state.active ? color : C.border}`,
                borderTop: `3px solid ${state.active ? color : '#d4c8bc'}`,
                borderRadius: 10,
                padding: '11px 14px 13px',
                boxShadow: state.active ? `0 0 0 3px ${color}18` : '0 1px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.18s',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', marginBottom: 10, userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={state.active}
                    onChange={e => { set(s => ({ ...s, active: e.target.checked })); if (e.target.checked) setLastActiveDateFilter(id); setPageDetail(1); }}
                    style={{ cursor: 'pointer', accentColor: color, width: 14, height: 14, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.65rem', fontWeight: 800, color: state.active ? color : '#aaa', textTransform: 'uppercase', letterSpacing: 0.8, transition: 'color 0.15s' }}>
                    {icon} {label}
                  </span>
                  {state.active && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.58rem', fontWeight: 700, color: '#fff', background: color, borderRadius: 8, padding: '2px 7px', letterSpacing: 0.3 }}>
                      Actif
                    </span>
                  )}
                </label>
                {/* Card désactivée tant que la case n'est pas cochée */}
                <div style={state.active ? undefined : { opacity: 0.4, pointerEvents: 'none' }}>
                {presence && (
                  <div style={{ display: 'flex', gap: 3, marginBottom: 10 }}>
                    {[['all', 'Personnalisé'], ['with', presence[0]], ['without', presence[1]]].map(([key, lbl]) => {
                      const on = datePresence[id] === key;
                      return (
                        <button
                          key={key}
                          onClick={() => { setDatePresence(p => ({ ...p, [id]: key })); setPage(1); setPageDetail(1); }}
                          style={{
                            flex: 1, padding: '3px 6px', fontSize: '0.62rem', fontWeight: 700,
                            border: `1px solid ${on ? color : C.border}`,
                            background: on ? color : '#fafafa',
                            color: on ? '#fff' : '#888',
                            borderRadius: 5, cursor: 'pointer', lineHeight: 1.5,
                            transition: 'all 0.15s',
                          }}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={noDate && state.active ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                  {[
                    { lbl: 'De', key: 'debut', val: state.debut, onChange: e => { const v = e.target.value; set(s => ({ ...s, debut: v, active: v ? true : s.active })); if (v) setLastActiveDateFilter(id); setPageDetail(1); } },
                    { lbl: 'À',  key: 'fin',   val: state.fin,   onChange: e => { const v = e.target.value; set(s => ({ ...s, fin:   v, active: v ? true : s.active })); if (v) setLastActiveDateFilter(id); setPageDetail(1); } },
                  ].map(({ lbl, key, val, onChange }) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                      <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', minWidth: 16, textAlign: 'right' }}>{lbl}</span>
                      <input
                        type="date"
                        value={val}
                        onChange={onChange}
                        style={{
                          flex: 1, height: 30, borderRadius: 6,
                          border: `1.5px solid ${val && state.active ? color : C.border}`,
                          padding: '0 8px', fontSize: '0.76rem', color: '#333',
                          background: val && state.active ? `${color}0a` : '#fafafa',
                          outline: 'none', transition: 'border-color 0.15s, background 0.15s',
                        }}
                      />
                    </div>
                  ))}
                  {(state.debut || state.fin) && (
                    <button
                      onClick={() => { set(getMonthRange()); setPageDetail(1); }}
                      style={{ marginTop: 4, fontSize: '0.63rem', fontWeight: 700, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', padding: 0, letterSpacing: 0.2 }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spinner animation="border" size="sm" style={{ color: C.primary }} />
            <span style={{ marginLeft: 10, color: '#888', fontSize: '0.85rem' }}>Chargement…</span>
          </div>
        )}

        {!loading && detail.length > 0 && (
          <>
            {/* ── Filtres cards groupés ────────────────────────────── */}
            <div style={{ marginBottom: 16, border: '1.5px solid #e4dfd8', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                label="Filtres"
                open={isOpen('filtres-cards')}
                onToggle={() => toggleSection('filtres-cards')}
                selectedCount={activeTranches.size + activeStatuts.size + activeTypes.size + activeStatutNotaire.size + activeNotaires.size + activeAnneesCompta.size + (activeTf !== null ? 1 : 0)}
              />
              {isOpen('filtres-cards') && (
                <div style={{ padding: '14px 14px 16px', borderTop: '1px solid #ede9e4', background: '#fff', display: 'flex', flexDirection: 'column', gap: 18 }}>

                  {/* Tranches */}
                  {trancheSummary.length > 1 && (
                    <div>
                      <SubFilterHeader
                        label="Tranches"
                        open={isOpen('sub-tranches')}
                        onToggle={() => toggleSection('sub-tranches')}
                        count={activeTranches.size}
                        extra={
                          <button
                            onClick={(e) => { e.stopPropagation(); const allSelected = trancheSummary.every(s => activeTranches.has(s.key)); setActiveTranches(allSelected ? new Set() : new Set(trancheSummary.map(s => s.key))); setPage(1); }}
                            style={{ fontSize: '0.58rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.primary, cursor: 'pointer' }}
                          >
                            {trancheSummary.every(s => activeTranches.has(s.key)) ? 'Tout désélectionner' : 'Tout sélectionner'}
                          </button>
                        }
                      />
                      {isOpen('sub-tranches') && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {trancheSummary.map(s => (
                            <TrancheChip
                              key={s.key}
                              label={isMultiProjet ? `${s.projet_nom} — ${s.tranche}` : s.tranche}
                              value={Number(s.nb).toLocaleString('fr-FR')}
                              active={activeTranches.has(s.key)}
                              onClick={() => toggleTranche(s.key)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Statut commercial */}
                  <div>
                    <SubFilterHeader
                      label="Statut commercial"
                      open={isOpen('sub-statut')}
                      onToggle={() => toggleSection('sub-statut')}
                      count={activeStatuts.size}
                    />
                    {isOpen('sub-statut') && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {statutSummary.map(s => {
                          const cfg = STATUT_CFG[s.statut] || STATUT_FALLBACK;
                          return (
                            <FilterCard
                              key={s.statut}
                              label={s.statut}
                              value={Number(s.nb).toLocaleString('fr-FR')}
                              color={cfg.color}
                              bg={cfg.bg}
                              border={cfg.border}
                              active={activeStatuts.has(s.statut)}
                              onClick={() => toggleStatut(s.statut)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Type de bien */}
                  <div>
                    <SubFilterHeader
                      label="Type de bien"
                      open={isOpen('sub-type')}
                      onToggle={() => toggleSection('sub-type')}
                      count={activeTypes.size}
                    />
                    {isOpen('sub-type') && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {typeSummary.map((s, i) => {
                          const color = typeColor(i);
                          return (
                            <FilterCard
                              key={s.type}
                              label={s.type}
                              value={Number(s.nb).toLocaleString('fr-FR')}
                              color={color}
                              bg={`${color}18`}
                              border={`${color}55`}
                              active={activeTypes.has(s.type)}
                              onClick={() => toggleType(s.type)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Statut notaire */}
                  <div>
                    <SubFilterHeader
                      label="Statut notaire"
                      open={isOpen('sub-statutnotaire')}
                      onToggle={() => toggleSection('sub-statutnotaire')}
                      count={activeStatutNotaire.size}
                    />
                    {isOpen('sub-statutnotaire') && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {statutNotaireSummary.map(s => {
                          const cfg = STATUT_NOTAIRE_CFG[s.key];
                          return (
                            <FilterCard
                              key={s.key}
                              label={cfg.label}
                              value={Number(s.nb).toLocaleString('fr-FR')}
                              color={cfg.color}
                              bg={cfg.bg}
                              border={cfg.border}
                              active={activeStatutNotaire.has(s.key)}
                              onClick={() => toggleStatutNotaire(s.key)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Notaire — ouvert par défaut */}
                  {notaireSummary.length > 1 && (
                    <div>
                      <SubFilterHeader
                        label="Notaire"
                        open={isOpen('sub-notaire')}
                        onToggle={() => toggleSection('sub-notaire')}
                        count={activeNotaires.size}
                      />
                      {isOpen('sub-notaire') && (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {notaireSummary.map((s, i) => {
                            const color = typeColor(i);
                            return (
                              <FilterCard
                                key={s.notaire}
                                label={s.notaire}
                                value={Number(s.nb).toLocaleString('fr-FR')}
                                color={color}
                                bg={`${color}18`}
                                border={`${color}55`}
                                active={activeNotaires.has(s.notaire)}
                                onClick={() => toggleNotaire(s.notaire)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Années de comptabilisation — ouvert par défaut */}
                  {anneeComptaSummary.length > 1 && (
                    <div>
                      <SubFilterHeader
                        label="Année de comptabilisation"
                        open={isOpen('sub-anneecompta')}
                        onToggle={() => toggleSection('sub-anneecompta')}
                        count={activeAnneesCompta.size}
                      />
                      {isOpen('sub-anneecompta') && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {anneeComptaSummary.map(s => (
                            <TrancheChip
                              key={s.annee}
                              label={s.annee}
                              value={Number(s.nb).toLocaleString('fr-FR')}
                              active={activeAnneesCompta.has(s.annee)}
                              onClick={() => toggleAnneeCompta(s.annee)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Titre foncier */}
                  <div>
                    <SubFilterHeader
                      label="Titre foncier"
                      open={isOpen('sub-tf')}
                      onToggle={() => toggleSection('sub-tf')}
                      count={activeTf !== null ? 1 : 0}
                    />
                    {isOpen('sub-tf') && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <FilterCard
                          label="Biens titrés (TF)"
                          value={tfSummary.titres.toLocaleString('fr-FR')}
                          color="#2e7d32" bg="#e8f5e9" border="#a5d6a7"
                          active={activeTf === 'titres'}
                          onClick={() => toggleTf('titres')}
                        />
                        <FilterCard
                          label="Biens non titrés"
                          value={tfSummary.non_titres.toLocaleString('fr-FR')}
                          color="#c62828" bg="#ffebee" border="#ef9a9a"
                          active={activeTf === 'non_titres'}
                          onClick={() => toggleTf('non_titres')}
                        />
                        <FilterCard
                          label="Total biens"
                          value={(tfSummary.titres + tfSummary.non_titres).toLocaleString('fr-FR')}
                          color="#4a505c" bg="#f5f4f2" border="#d4c8bc"
                          active={false} onClick={undefined}
                        />
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* ── Indicateur filtre actif ──────────────────────────── */}
            {hasFilter && (
              <div style={{ fontSize: '0.72rem', color: '#e65100', fontWeight: 600, background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 6, padding: '5px 12px', marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                ⚡ Filtre actif — {filteredTranches.reduce((s, l) => s + l.nb_biens, 0).toLocaleString('fr-FR')} bien{filteredTranches.reduce((s, l) => s + l.nb_biens, 0) !== 1 ? 's' : ''} affichés
              </div>
            )}

            {/* ── Tableau tranches ─────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a505c', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Coût par tranche
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <UnitToggle value={unitScale} onChange={setUnitScale} />
                <button
                  onClick={exportExcelCouts}
                  disabled={filteredTranches.length === 0}
                  style={{ height: 30, padding: '0 14px', borderRadius: 6, cursor: filteredTranches.length === 0 ? 'not-allowed' : 'pointer', background: '#fff', color: '#2e7d32', border: '1.5px solid #a5d6a7', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: filteredTranches.length === 0 ? 0.5 : 1 }}
                >
                  ⬇ Excel
                </button>
              </div>
            </div>
            {filteredTranches.length > 0 ? (
              <div style={{ borderRadius: 8, border: '1.5px solid #ddd8d0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(180deg,#f7f5f2,#f0ede8)' }}>
                        {isMultiProjet && <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left' }}>Projet</th>}
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', borderRight: '1px solid #ddd8d0', paddingLeft: 14 }}>Tranche</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Nb biens</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Superficie (m²)</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', minWidth: 140, color: C.primary }}>Coût / m²</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', borderLeft: '1px solid #ddd8d0', color: C.primary, paddingRight: 14, background: '#edf7ed' }}>Montant estimé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedTranches.map((l, i) => {
                        const coutVal = parseFloat((couts[trancheKey(l.projet_id, l.tranche)] || '').replace(',', '.')) || 0;
                        const montant = l.cout_total;
                        const hasCout = coutVal > 0 || l.cout_total > 0;
                        const types = Object.values(l.types).sort((a, b) => a.type.localeCompare(b.type, 'fr'));
                        const nbTypesSaisis = types.filter(t => parseCout(coutsType[typeKey(l.projet_id, l.tranche, t.type)]) > 0).length;
                        const expanded = expandedTranches.has(l.key);
                        return (
                          <React.Fragment key={l.key}>
                          <tr
                            style={{ background: i % 2 === 0 ? '#fff' : '#faf9f8' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f4f8f4'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#faf9f8'; }}
                          >
                            {isMultiProjet && <td style={{ ...TD, fontSize: '0.68rem', color: '#888' }}>{l.projet_nom}</td>}
                            <td style={{ ...TD, fontSize: '0.68rem', fontWeight: 700, color: '#3d3d3d', borderRight: '1px solid #ede9e4', paddingLeft: 14 }}>
                              {l.tranche}
                              <button
                                onClick={() => setExpandedTranches(prev => { const n = new Set(prev); n.has(l.key) ? n.delete(l.key) : n.add(l.key); return n; })}
                                title="Saisir un coût/m² par type de bien dans cette tranche"
                                style={{ marginLeft: 8, padding: '1px 7px', fontSize: '0.58rem', fontWeight: 700, borderRadius: 10, cursor: 'pointer', border: `1px solid ${nbTypesSaisis > 0 ? C.primary : C.border}`, background: expanded ? C.primary : (nbTypesSaisis > 0 ? C.light : '#fff'), color: expanded ? '#fff' : C.primary }}
                              >
                                {expanded ? '▾' : '▸'} par type{nbTypesSaisis > 0 ? ` · ${nbTypesSaisis}/${types.length}` : ''}
                              </button>
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}>{l.nb_biens.toLocaleString('fr-FR')}</td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}>{fmtN(l.superficie_totale)}</td>
                            <td style={{ ...TD, fontSize: '0.68rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: `1.5px solid ${hasCout ? C.primary : '#d8d2ca'}`, borderRadius: 6, overflow: 'hidden', width: 120, background: hasCout ? '#f4fbf4' : '#fafaf8', transition: 'all 0.15s' }}>
                                <input
                                  type="number"
                                  value={couts[trancheKey(l.projet_id, l.tranche)] ?? ''}
                                  onChange={e => handleCout(l.projet_id, l.tranche, e.target.value)}
                                  onBlur={e => saveCout(l.projet_id, l.tranche, e.target.value)}
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                  style={{ flex: 1, height: 26, border: 'none', padding: '0 7px', fontSize: '0.68rem', width: 0, outline: 'none', background: 'transparent', color: hasCout ? C.primary : '#555', fontWeight: hasCout ? 700 : 400 }}
                                />
                                <span style={{ fontSize: '0.58rem', fontWeight: 700, color: hasCout ? C.primary : '#bbb', padding: '0 6px', background: hasCout ? '#e0f2e0' : '#f0ede8', height: 26, display: 'flex', alignItems: 'center', borderLeft: `1px solid ${hasCout ? C.primary : '#d8d2ca'}`, whiteSpace: 'nowrap' }}>DH</span>
                              </div>
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: hasCout ? 700 : 400, borderLeft: '1px solid #ede9e4', paddingRight: 14, color: hasCout ? C.primary : '#ccc', background: hasCout ? '#f8fdf8' : 'transparent' }}>
                              {hasCout ? fmtMoney(montant) : '—'}
                            </td>
                          </tr>
                          {/* Sous-lignes : coût par type, rattaché à cette tranche (vide = reprend le coût de la tranche) */}
                          {expanded && types.map(t => {
                            const val     = coutsType[typeKey(l.projet_id, l.tranche, t.type)];
                            const ownCout = parseCout(val) > 0;
                            return (
                              <tr key={`${l.key}-${t.type}`} style={{ background: '#f7fbf7' }}>
                                {isMultiProjet && <td style={{ ...TD }}></td>}
                                <td style={{ ...TD, fontSize: '0.66rem', color: '#555', borderRight: '1px solid #ede9e4', paddingLeft: 30 }}>
                                  └ {t.type}
                                </td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', color: '#888' }}>{t.nb_biens.toLocaleString('fr-FR')}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', color: '#888' }}>{fmtN(t.superficie_totale)}</td>
                                <td style={{ ...TD, fontSize: '0.66rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${ownCout ? C.primary : '#e4dfd8'}`, borderRadius: 6, overflow: 'hidden', width: 120, background: ownCout ? '#f4fbf4' : '#fff' }}>
                                    <input
                                      type="number"
                                      value={val ?? ''}
                                      onChange={e => handleCoutType(l.projet_id, l.tranche, t.type, e.target.value)}
                                      onBlur={e => saveCoutType(l.projet_id, l.tranche, t.type, e.target.value)}
                                      placeholder={coutVal > 0 ? `${fmtN(coutVal)} (tranche)` : '0.00'}
                                      min="0"
                                      step="0.01"
                                      style={{ flex: 1, height: 24, border: 'none', padding: '0 7px', fontSize: '0.66rem', width: 0, outline: 'none', background: 'transparent', color: ownCout ? C.primary : '#555', fontWeight: ownCout ? 700 : 400 }}
                                    />
                                    <span style={{ fontSize: '0.56rem', fontWeight: 700, color: ownCout ? C.primary : '#bbb', padding: '0 6px', height: 24, display: 'flex', alignItems: 'center', borderLeft: `1px solid ${ownCout ? C.primary : '#e4dfd8'}` }}>DH</span>
                                  </div>
                                </td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', borderLeft: '1px solid #ede9e4', paddingRight: 14, color: t.cout_total > 0 ? (ownCout ? C.primary : '#888') : '#ccc' }}>
                                  {t.cout_total > 0 ? fmtMoney(t.cout_total) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #c8dfc8', background: 'linear-gradient(180deg,#edf7ed,#e4f2e4)' }}>
                        {isMultiProjet && <td style={{ ...TD }}></td>}
                        <td style={{ ...TD, fontSize: '0.65rem', fontWeight: 800, color: C.primary, borderRight: '1px solid #c8dfc8', paddingLeft: 14, letterSpacing: 0.5 }}>TOTAL</td>
                        <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}>{totalBiens.toLocaleString('fr-FR')}</td>
                        <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}>{fmtN(totalSuperficie)}</td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, borderLeft: '1px solid #c8dfc8', paddingRight: 14, color: totalMontant > 0 ? C.primary : '#ccc' }}>
                          {totalMontant > 0 ? fmtMoney(totalMontant) : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* ── Pagination ──────────────────────────────────── */}
                {filteredTranches.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #ede9e4', background: '#faf9f7', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                        <span style={{ color: '#4a505c', fontWeight: 600 }}>
                          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredTranches.length)}
                        </span>{' '}sur {filteredTranches.length}
                      </span>
                      <select
                        value={pageSize}
                        onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        style={{ height: 22, borderRadius: 4, border: '1px solid #d4c8bc', fontSize: '0.68rem', color: '#4a505c', background: '#fff', padding: '0 4px', cursor: 'pointer' }}
                      >
                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                      </select>
                    </div>
                    {filteredTranches.length > pageSize && (
                      <div style={{ display: 'flex', gap: 3 }}>
                        {[{ l: '«', t: 1 }, { l: '‹', t: page - 1 }, { l: '›', t: page + 1 }, { l: '»', t: totalPages }].map(({ l, t }) => (
                          <button key={l}
                            onClick={() => setPage(Math.max(1, Math.min(totalPages, t)))}
                            disabled={t < 1 || t > totalPages || t === page}
                            style={{ minWidth: 26, height: 24, border: '1px solid #d4c8bc', borderRadius: 5, background: t === page ? '#4a505c' : '#fff', color: t === page ? '#fff' : '#4a505c', cursor: (t < 1 || t > totalPages || t === page) ? 'default' : 'pointer', fontSize: '0.72rem', opacity: (t < 1 || t > totalPages) ? 0.3 : 1 }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', padding: 30 }}>
                Aucune tranche ne correspond aux filtres sélectionnés.
              </p>
            )}

            {/* ── Actions coûts ──────────────────────────────────────── */}
            {filteredTranches.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={saveAllCouts}
                  disabled={savingAll || (Object.keys(couts).length + Object.keys(coutsType).length) === 0}
                  style={{ height: 32, padding: '0 16px', borderRadius: 7, cursor: (savingAll || (Object.keys(couts).length + Object.keys(coutsType).length) === 0) ? 'not-allowed' : 'pointer', background: C.primary, color: '#fff', border: 'none', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: (savingAll || (Object.keys(couts).length + Object.keys(coutsType).length) === 0) ? 0.6 : 1 }}
                >
                  {savingAll ? '…' : '💾'} Sauvegarder tout
                </button>
                <button
                  onClick={reloadCouts}
                  style={{ height: 32, padding: '0 16px', borderRadius: 7, cursor: 'pointer', background: '#fff', color: '#546e7a', border: '1.5px solid #b0bec5', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  ↺ Recharger depuis BD
                </button>
                {saveMsg === 'ok'     && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#2e7d32' }}>✔ Sauvegardé</span>}
                {saveMsg === 'err'    && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#c62828' }}>✘ Erreur lors de la sauvegarde</span>}
                {saveMsg === 'reload' && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#546e7a' }}>↺ Coûts rechargés depuis la base</span>}
              </div>
            )}

            {/* ── CA comptable (paramètres propres, hors filtres du haut) ── */}
            <div style={{ marginTop: 32, borderRadius: 8, border: '1.5px solid #ddd8d0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: '8px 14px', background: '#f4fbf4', borderBottom: '1px solid #ddd8d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: '0.68rem', fontWeight: 800, color: C.primary, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    CA comptable {caCompta.annee}
                  </span>
                  <div style={{ fontSize: '0.6rem', color: '#999', marginTop: 2 }}>
                    Biens signés notaire · indépendant des filtres du haut
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <UnitToggle value={unitScale} onChange={setUnitScale} />
                  <button
                    onClick={exportExcelCaCompta}
                    style={{ height: 26, padding: '0 12px', borderRadius: 6, cursor: 'pointer', background: '#fff', color: '#2e7d32', border: '1.5px solid #a5d6a7', fontSize: '0.68rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    ⬇ Excel
                  </button>
                </div>
              </div>

              {/* Paramètres */}
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 14px', borderBottom: '1px solid #ede9e4', background: '#fff' }}>
                <div>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 4 }}>Année comptable</div>
                  <input
                    type="number"
                    value={caCompta.annee}
                    min="2000"
                    max="2100"
                    onChange={e => {
                      const y = parseInt(e.target.value, 10);
                      if (y >= 2000 && y <= 2100) setCaCompta(getCaComptaDefaults(y));
                      else setCaCompta(s => ({ ...s, annee: e.target.value }));
                    }}
                    style={{ width: 90, height: 30, borderRadius: 6, border: `1.5px solid ${C.border}`, padding: '0 8px', fontSize: '0.78rem', fontWeight: 700, color: C.primary }}
                  />
                </div>
                {[
                  { lbl: 'Période N',  d: 'nDebut',  f: 'nFin' },
                  { lbl: 'Année N-1',  d: 'n1Debut', f: 'n1Fin' },
                ].map(({ lbl, d, f }) => (
                  <div key={d}>
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 4 }}>{lbl} · signé notaire</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {[d, f].map((k, j) => (
                        <React.Fragment key={k}>
                          {j === 1 && <span style={{ fontSize: '0.65rem', color: '#bbb' }}>→</span>}
                          <input
                            type="date"
                            value={caCompta[k]}
                            onChange={e => { const v = e.target.value; setCaCompta(s => ({ ...s, [k]: v })); }}
                            style={{ height: 30, borderRadius: 6, border: `1.5px solid ${C.border}`, padding: '0 8px', fontSize: '0.74rem', color: '#333', background: '#fafafa' }}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setCaCompta(getCaComptaDefaults(new Date().getFullYear()))}
                  style={{ height: 30, padding: '0 10px', fontSize: '0.63rem', fontWeight: 700, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  ↺ Par défaut
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(180deg,#f7f5f2,#f0ede8)' }}>
                      <th rowSpan={2} style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', paddingLeft: 14, borderRight: '1px solid #ddd8d0' }}>Signé notaire</th>
                      <th rowSpan={2} style={{ ...TH, fontSize: '0.58rem', textAlign: 'center', borderRight: '1px solid #ddd8d0' }}>Période</th>
                      {CC_GROUPS.map(g => (
                        <th key={g.key} colSpan={g.pct ? 5 : 4} style={{ ...TH, fontSize: '0.58rem', textAlign: 'center', color: g.color, background: g.bg, borderBottom: `1px solid ${g.border}`, borderLeft: '1px solid #ddd8d0' }}>{g.label}</th>
                      ))}
                    </tr>
                    <tr style={{ background: 'linear-gradient(180deg,#f7f5f2,#f0ede8)' }}>
                      {CC_GROUPS.flatMap(g =>
                        ['Nb', 'CA', 'Coût', 'Marge', ...(g.pct ? ['%'] : [])].map((lbl, j) => (
                          <th key={`${g.key}-${lbl}`} style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: g.color, background: g.bg, borderLeft: j === 0 ? '1px solid #ddd8d0' : undefined }}>{lbl}</th>
                        ))
                      )}
                    </tr>
                  </thead>
                  {[{ part: 'body', rows: caComptaRows }, { part: 'foot', rows: [caComptaTotal] }].map(({ part, rows }) => {
                    const isFoot = part === 'foot';
                    const Wrap = isFoot ? 'tfoot' : 'tbody';
                    return (
                      <Wrap key={part}>
                        {rows.map((r, i) => (
                          <tr key={r.key} style={isFoot ? { background: '#f4fbf4', borderTop: '2px solid #c8dfc8' } : { background: i % 2 === 0 ? '#fff' : '#faf9f8' }}>
                            {isFoot ? (
                              <td colSpan={2} style={{ ...TD, fontSize: '0.65rem', fontWeight: 800, color: C.primary, paddingLeft: 14, letterSpacing: 0.5, borderRight: '1px solid #c8dfc8' }}>TOTAL</td>
                            ) : (
                              <>
                                <td style={{ ...TD, fontSize: '0.7rem', fontWeight: 800, color: '#3d3d3d', paddingLeft: 14, borderRight: '1px solid #ede9e4' }}>{r.label}</td>
                                <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'center', color: '#666', borderRight: '1px solid #ede9e4', whiteSpace: 'nowrap' }}>
                                  {fmtDate(r.debut)} → {fmtDate(r.fin)}
                                  {r.autre.nb > 0 && (
                                    <div style={{ fontSize: '0.58rem', color: '#aaa', marginTop: 2, whiteSpace: 'normal' }}>
                                      <ClickNum list={r.autre.list} title={`${r.label} · comptabilisés une autre année`}>{r.autre.nb}</ClickNum> bien{r.autre.nb > 1 ? 's' : ''} comptabilisé{r.autre.nb > 1 ? 's' : ''} une autre année (exclu{r.autre.nb > 1 ? 's' : ''})
                                    </div>
                                  )}
                                </td>
                              </>
                            )}
                            {CC_GROUPS.flatMap(g => {
                              const v = r[g.key];
                              const marge = v.ca - v.cout;
                              const pct = v.ca > 0 ? (marge / v.ca) * 100 : null;
                              const mColor = v.cout === 0 ? '#ccc' : marge > 0 ? '#2e7d32' : marge < 0 ? '#c62828' : '#ccc';
                              const title = `${isFoot ? 'Total' : r.label} · ${g.title}`;
                              const base = { ...TD, fontSize: isFoot ? '0.7rem' : '0.68rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: isFoot ? 800 : 400, background: g.key === 'total' && !isFoot ? '#f7f6f3' : undefined };
                              return [
                                <td key={`${g.key}-nb`} style={{ ...base, color: '#4a505c', borderLeft: '1px solid #ede9e4' }}><ClickNum list={v.list} title={title}>{v.nb.toLocaleString('fr-FR')}</ClickNum></td>,
                                <td key={`${g.key}-ca`} style={{ ...base, fontWeight: 700, color: v.ca > 0 ? g.color : '#ccc' }}><ClickNum list={v.list} title={title}>{v.ca > 0 ? fmtMoney(v.ca) : '—'}</ClickNum></td>,
                                <td key={`${g.key}-cout`} style={{ ...base, color: v.cout > 0 ? '#4a505c' : '#ccc' }}><ClickNum list={v.list} title={title}>{v.cout > 0 ? fmtMoney(v.cout) : '—'}</ClickNum></td>,
                                <td key={`${g.key}-marge`} style={{ ...base, fontWeight: 700, color: mColor, paddingRight: g.pct ? undefined : 10 }}>{v.cout > 0 ? fmtMoney(marge) : '—'}</td>,
                                ...(g.pct ? [<td key={`${g.key}-pct`} style={{ ...base, fontWeight: 700, color: mColor, paddingRight: 14 }}>{v.cout > 0 && pct != null ? `${pct.toFixed(1)}%` : '—'}</td>] : []),
                              ];
                            })}
                          </tr>
                        ))}
                      </Wrap>
                    );
                  })}
                </table>
                {caComptaTotal.sansCout.length > 0 && (
                  <div style={{ padding: '8px 14px', fontSize: '0.68rem', color: '#c62828', background: '#fff5f5', borderTop: '1px solid #f3d0d0' }}>
                    ⚠ <ClickNum list={caComptaTotal.sansCout} title="biens sans coût/m² saisi">{caComptaTotal.sansCout.length} bien{caComptaTotal.sansCout.length > 1 ? 's' : ''}</ClickNum> sans coût/m² saisi sur leur tranche — comptés à 0 dans le coût, la marge est surestimée.
                  </div>
                )}
              </div>
            </div>

            {/* ── Tableau CA par tranche ───────────────────────────── */}
            {filteredTranches.length > 0 && (
              <div style={{ marginTop: 32, borderRadius: 8, border: '1.5px solid #ddd8d0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ padding: '8px 14px', background: '#f4fbf4', borderBottom: '1px solid #ddd8d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 800, color: C.primary, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Chiffre d'affaires par tranche
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <UnitToggle value={unitScale} onChange={setUnitScale} />
                    <button
                      onClick={exportExcelCA}
                      style={{ height: 26, padding: '0 12px', borderRadius: 6, cursor: 'pointer', background: '#fff', color: '#2e7d32', border: '1.5px solid #a5d6a7', fontSize: '0.68rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
                    >
                      ⬇ Excel
                    </button>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(180deg,#f7f5f2,#f0ede8)' }}>
                        {isMultiProjet && <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left' }}>Projet</th>}
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', borderRight: '1px solid #ddd8d0', paddingLeft: 14 }}>Tranche</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Nb biens</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Superficie (m²)</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', minWidth: 140, color: C.primary }}>Coût / m²</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary }}>Coût total</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', borderLeft: '1px solid #ddd8d0', color: C.primary, background: '#edf7ed' }}>CA (contrat.PRIX_NET)</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary, background: '#edf7ed' }}>Marge</th>
                        <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary, paddingRight: 14, background: '#edf7ed' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTranches.map((l, i) => {
                        const coutVal  = parseFloat((couts[trancheKey(l.projet_id, l.tranche)] || '').replace(',', '.')) || 0;
                        const hasCout  = coutVal > 0 || l.cout_total > 0;
                        const montant  = l.cout_total;
                        const marge    = l.ca_totale - montant;
                        const margePct = l.ca_totale > 0 ? (marge / l.ca_totale) * 100 : null;
                        const margeColor = marge > 0 ? '#2e7d32' : (marge < 0 ? '#c62828' : '#ccc');
                        const types    = Object.values(l.types).sort((a, b) => a.type.localeCompare(b.type, 'fr'));
                        const expanded = expandedCaTranches.has(l.key);
                        return (
                          <React.Fragment key={l.key}>
                          <tr style={{ background: i % 2 === 0 ? '#fff' : '#faf9f8' }}>
                            {isMultiProjet && <td style={{ ...TD, fontSize: '0.68rem', color: '#888' }}>{l.projet_nom}</td>}
                            <td style={{ ...TD, fontSize: '0.68rem', fontWeight: 700, color: '#3d3d3d', borderRight: '1px solid #ede9e4', paddingLeft: 14 }}>
                              {l.tranche}
                              <button
                                onClick={() => setExpandedCaTranches(prev => { const n = new Set(prev); n.has(l.key) ? n.delete(l.key) : n.add(l.key); return n; })}
                                title="Détail CA / coût / marge par type de bien dans cette tranche"
                                style={{ marginLeft: 8, padding: '1px 7px', fontSize: '0.58rem', fontWeight: 700, borderRadius: 10, cursor: 'pointer', border: `1px solid ${expanded ? C.primary : C.border}`, background: expanded ? C.primary : '#fff', color: expanded ? '#fff' : C.primary }}
                              >
                                {expanded ? '▾' : '▸'} par type
                              </button>
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}>{l.nb_biens.toLocaleString('fr-FR')}</td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}>{fmtN(l.superficie_totale)}</td>
                            <td style={{ ...TD, fontSize: '0.68rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: `1.5px solid ${hasCout ? C.primary : '#d8d2ca'}`, borderRadius: 6, overflow: 'hidden', width: 120, background: hasCout ? '#f4fbf4' : '#fafaf8', transition: 'all 0.15s' }}>
                                <input
                                  type="number"
                                  value={couts[trancheKey(l.projet_id, l.tranche)] ?? ''}
                                  onChange={e => handleCout(l.projet_id, l.tranche, e.target.value)}
                                  onBlur={e => saveCout(l.projet_id, l.tranche, e.target.value)}
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                  style={{ flex: 1, height: 26, border: 'none', padding: '0 7px', fontSize: '0.68rem', width: 0, outline: 'none', background: 'transparent', color: hasCout ? C.primary : '#555', fontWeight: hasCout ? 700 : 400 }}
                                />
                                <span style={{ fontSize: '0.58rem', fontWeight: 700, color: hasCout ? C.primary : '#bbb', padding: '0 6px', background: hasCout ? '#e0f2e0' : '#f0ede8', height: 26, display: 'flex', alignItems: 'center', borderLeft: `1px solid ${hasCout ? C.primary : '#d8d2ca'}`, whiteSpace: 'nowrap' }}>DH</span>
                              </div>
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: hasCout ? 700 : 400, color: hasCout ? C.primary : '#ccc' }}>
                              {hasCout ? fmtMoney(montant) : '—'}
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, borderLeft: '1px solid #ede9e4', color: l.ca_totale > 0 ? C.primary : '#ccc', background: l.ca_totale > 0 ? '#f8fdf8' : 'transparent' }}>
                              {l.ca_totale > 0 ? fmtMoney(l.ca_totale) : '—'}
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, color: margeColor, background: '#f8fdf8' }}>
                              {hasCout && l.ca_totale > 0 ? fmtMoney(marge) : '—'}
                            </td>
                            <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, color: margeColor, paddingRight: 14, background: '#f8fdf8' }}>
                              {hasCout && margePct != null ? `${margePct.toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                          {/* Sous-lignes par type (coût du type dans cette tranche, vide = coût de la tranche) */}
                          {expanded && types.map(t => {
                            const val      = coutsType[typeKey(l.projet_id, l.tranche, t.type)];
                            const ownCout  = parseCout(val) > 0;
                            const tCost    = t.cout_total > 0;
                            const tMarge   = t.ca_totale - t.cout_total;
                            const tPct     = t.ca_totale > 0 ? (tMarge / t.ca_totale) * 100 : null;
                            const tColor   = tMarge > 0 ? '#2e7d32' : (tMarge < 0 ? '#c62828' : '#ccc');
                            return (
                              <tr key={`${l.key}-${t.type}`} style={{ background: '#f7fbf7' }}>
                                {isMultiProjet && <td style={{ ...TD }}></td>}
                                <td style={{ ...TD, fontSize: '0.66rem', color: '#555', borderRight: '1px solid #ede9e4', paddingLeft: 30 }}>└ {t.type}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', color: '#888' }}>{t.nb_biens.toLocaleString('fr-FR')}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', color: '#888' }}>{fmtN(t.superficie_totale)}</td>
                                <td style={{ ...TD, fontSize: '0.66rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${ownCout ? C.primary : '#e4dfd8'}`, borderRadius: 6, overflow: 'hidden', width: 120, background: ownCout ? '#f4fbf4' : '#fff' }}>
                                    <input
                                      type="number"
                                      value={val ?? ''}
                                      onChange={e => handleCoutType(l.projet_id, l.tranche, t.type, e.target.value)}
                                      onBlur={e => saveCoutType(l.projet_id, l.tranche, t.type, e.target.value)}
                                      placeholder={coutVal > 0 ? `${fmtN(coutVal)} (tranche)` : '0.00'}
                                      min="0"
                                      step="0.01"
                                      style={{ flex: 1, height: 24, border: 'none', padding: '0 7px', fontSize: '0.66rem', width: 0, outline: 'none', background: 'transparent', color: ownCout ? C.primary : '#555', fontWeight: ownCout ? 700 : 400 }}
                                    />
                                    <span style={{ fontSize: '0.56rem', fontWeight: 700, color: ownCout ? C.primary : '#bbb', padding: '0 6px', height: 24, display: 'flex', alignItems: 'center', borderLeft: `1px solid ${ownCout ? C.primary : '#e4dfd8'}` }}>DH</span>
                                  </div>
                                </td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', color: tCost ? (ownCout ? C.primary : '#888') : '#ccc' }}>{tCost ? fmtMoney(t.cout_total) : '—'}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', fontWeight: 600, borderLeft: '1px solid #ede9e4', color: t.ca_totale > 0 ? C.primary : '#ccc' }}>{t.ca_totale > 0 ? fmtMoney(t.ca_totale) : '—'}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', fontWeight: 600, color: tColor }}>{tCost && t.ca_totale > 0 ? fmtMoney(tMarge) : '—'}</td>
                                <td style={{ ...TD, fontSize: '0.66rem', textAlign: 'right', fontWeight: 600, color: tColor, paddingRight: 14 }}>{tCost && tPct != null ? `${tPct.toFixed(1)}%` : '—'}</td>
                              </tr>
                            );
                          })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #c8dfc8', background: 'linear-gradient(180deg,#edf7ed,#e4f2e4)' }}>
                        {isMultiProjet && <td style={{ ...TD }}></td>}
                        <td style={{ ...TD, fontSize: '0.65rem', fontWeight: 800, color: C.primary, borderRight: '1px solid #c8dfc8', paddingLeft: 14, letterSpacing: 0.5 }}>TOTAL</td>
                        <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}>{totalBiens.toLocaleString('fr-FR')}</td>
                        <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}>{fmtN(totalSuperficie)}</td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: totalMontant > 0 ? C.primary : '#ccc' }}>
                          {totalMontant > 0 ? fmtMoney(totalMontant) : '—'}
                        </td>
                        <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, borderLeft: '1px solid #c8dfc8', color: totalCA > 0 ? C.primary : '#ccc' }}>
                          {totalCA > 0 ? fmtMoney(totalCA) : '—'}
                        </td>
                        <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, color: totalMarge > 0 ? '#2e7d32' : (totalMarge < 0 ? '#c62828' : '#ccc') }}>
                          {totalMontant > 0 && totalCA > 0 ? fmtMoney(totalMarge) : '—'}
                        </td>
                        <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, paddingRight: 14, color: totalMarge > 0 ? '#2e7d32' : (totalMarge < 0 ? '#c62828' : '#ccc') }}>
                          {totalMontant > 0 && totalMargePct != null ? `${totalMargePct.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* ── Filtres statut notaire au-dessus du détail ───────── */}
            {statutNotaireSummary.some(s => s.nb > 0) && (
              <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.8, flexShrink: 0 }}>Statut notaire</span>
                {statutNotaireSummary.map(s => {
                  const cfg = STATUT_NOTAIRE_CFG[s.key];
                  return (
                    <FilterCard
                      key={s.key}
                      label={cfg.label}
                      value={Number(s.nb).toLocaleString('fr-FR')}
                      color={cfg.color}
                      bg={cfg.bg}
                      border={cfg.border}
                      active={activeStatutNotaire.has(s.key)}
                      onClick={() => toggleStatutNotaire(s.key)}
                    />
                  );
                })}
                {activeStatutNotaire.size > 0 && (
                  <button onClick={() => { setActiveStatutNotaire(new Set()); resetPages(); }}
                    style={{ fontSize: '0.63rem', fontWeight: 700, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', flexShrink: 0 }}>
                    ✕ Tout
                  </button>
                )}
              </div>
            )}

            {/* ── Tableau détail biens ─────────────────────────────── */}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a505c', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Détail des biens
                  <span style={{ marginLeft: 8, fontSize: '0.68rem', fontWeight: 400, color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>
                    {filteredBiens.length.toLocaleString('fr-FR')} bien{filteredBiens.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <UnitToggle value={unitScale} onChange={setUnitScale} />
                  <button
                    onClick={exportExcel}
                    disabled={filteredBiens.length === 0}
                    style={{ height: 30, padding: '0 14px', borderRadius: 6, cursor: filteredBiens.length === 0 ? 'not-allowed' : 'pointer', background: '#fff', color: '#2e7d32', border: '1.5px solid #a5d6a7', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: filteredBiens.length === 0 ? 0.5 : 1 }}
                  >
                    ⬇ Excel
                  </button>
                </div>
              </div>
              <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                    <thead>
                      <tr>
                        <SortTH col="id"                     label="ID Bien"          align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="numero"                 label="N°"               align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        {isMultiProjet && <SortTH col="projet_nom" label="Projet" align="left" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />}
                        <SortTH col="contrat_id"             label="ID Vente"         align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="statut"                 label="Statut"           align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="statut_notaire"         label="Statut notaire"   align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="designation"            label="Désignation"      align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="type"                   label="Type"             align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="tranche"                label="Tranche"          align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="superficie"             label="Superficie (m²)"  align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="prix_m"                 label="Prix/m² (DB)"     align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="prix_vente"             label="Prix vente"       align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="bien_prix_m"            label="Bien Prix/m²"     align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#546e7a' }} />
                        <SortTH col="bien_prix_vente"        label="Bien Prix vente"  align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#546e7a' }} />
                        <SortTH col="ca"                     label="CA"               align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: C.primary }} />
                        <SortTH col="date_signature_client"  label="Signé client"     align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#546e7a' }} />
                        <SortTH col="date_en_cours"          label="Sign. en cours"   align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#e65100' }} />
                        <SortTH col="date_signature_notaire" label="Signé notaire"    align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#2e7d32' }} />
                        <SortTH col="date_minute"            label="Enreg. minute"    align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#6a1b9a' }} />
                        <SortTH col="date_expedition"        label="Envoi minute"     align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#ad1457' }} />
                        <SortTH col="date_compta"            label="Compta"           align="center" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: '#6d4e36' }} />
                        <SortTH col="notaire"                label="Notaire"          align="left"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortTH col="cout_m2"                label="Coût/m² saisi"   align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: C.primary }} />
                        <SortTH col="cout_total"             label="Coût total"       align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: C.primary }} />
                        <SortTH col="marge"                  label="Marge"            align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: C.primary }} />
                        <SortTH col="margepct"               label="%"                align="right"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} style={{ color: C.primary }} />
                      </tr>
                    </thead>
                    <tbody>
                      {pagedBiens.map((b, i) => {
                        const coutM2   = coutM2Of(b);
                        const coutTot  = (Number(b.superficie) || 0) * coutM2;
                        const caBien   = Number(b.ca) || 0;
                        const margeBien    = caBien - coutTot;
                        const margePctBien = caBien > 0 ? (margeBien / caBien) * 100 : null;
                        const margeBienColor = margeBien > 0 ? '#2e7d32' : (margeBien < 0 ? '#c62828' : '#ccc');
                        const cfg     = STATUT_CFG[b.statut] || STATUT_FALLBACK;
                        return (
                          <tr key={b.id}
                            style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f0ede8'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'; }}
                          >
                            <td style={{ ...TD, textAlign: 'center', color: '#888', fontWeight: 600 }}>{b.id}</td>
                            <td style={{ ...TD, textAlign: 'center', fontWeight: 600 }}>{b.numero || '—'}</td>
                            {isMultiProjet && <td style={{ ...TD, color: '#888' }}>{b.projet_nom}</td>}
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {b.contrat_id
                                ? <a href={`https://shems.ngrok.io/consultation/${b.contrat_id}`} target="_blank" rel="noreferrer"
                                    style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>
                                    #{b.contrat_id}
                                  </a>
                                : <span style={{ color: '#ccc' }}>—</span>
                              }
                            </td>
                            <td style={{ ...TD }}>
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                {b.statut}
                              </span>
                            </td>
                            <td style={{ ...TD }}>
                              {(() => { const snk = b.statut_notaire || 'NOT_YET'; const sncfg = STATUT_NOTAIRE_CFG[snk] || STATUT_NOTAIRE_CFG.NOT_YET; return (
                                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: sncfg.color, background: sncfg.bg, border: `1px solid ${sncfg.border}`, borderRadius: 10, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                  {sncfg.label}
                                </span>
                              ); })()}
                            </td>
                            <td style={{ ...TD, fontWeight: 600, color: '#4a505c', whiteSpace: 'nowrap' }}>{b.designation || '—'}</td>
                            <td style={{ ...TD, whiteSpace: 'nowrap' }}>{b.type || '—'}</td>
                            <td style={{ ...TD, whiteSpace: 'nowrap' }}>{b.tranche}</td>
                            <td style={{ ...TD, textAlign: 'right' }}>{b.superficie != null ? fmtN(b.superficie) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', color: '#546e7a' }}>{b.prix_m != null ? fmtN(b.prix_m) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{b.prix_vente != null ? fmtMoney(b.prix_vente) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', color: '#546e7a' }}>{b.bien_prix_m != null ? fmtN(b.bien_prix_m) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', color: '#546e7a' }}>{b.bien_prix_vente != null ? fmtMoney(b.bien_prix_vente) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: caBien > 0 ? C.primary : '#ccc' }}>{caBien > 0 ? fmtMoney(caBien) : '—'}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#546e7a' }}>{fmtDate(b.date_signature_client)}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#e65100' }}>{fmtDate(b.date_en_cours)}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#2e7d32' }}>{fmtDate(b.date_signature_notaire)}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#6a1b9a' }}>{fmtDate(b.date_minute)}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#ad1457' }}>{fmtDate(b.date_expedition)}</td>
                            <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.7rem', color: '#6d4e36', fontWeight: 600 }} title={b.date_compta ? fmtDate(b.date_compta) : ''}>
                              {b.date_compta ? new Date(b.date_compta).getFullYear() : '—'}
                            </td>
                            <td style={{ ...TD, whiteSpace: 'nowrap' }}>{b.notaire || '—'}</td>
                            <td style={{ ...TD, textAlign: 'right', color: coutM2 > 0 ? C.primary : '#ccc', fontWeight: 600 }}>
                              {coutM2 > 0 ? fmtN(coutM2) : '—'}
                            </td>
                            <td style={{ ...TD, textAlign: 'right', color: coutTot > 0 ? C.primary : '#ccc', fontWeight: 700 }}>
                              {coutTot > 0 ? fmtMoney(coutTot) : '—'}
                            </td>
                            <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: margeBienColor }}>
                              {coutTot > 0 && caBien > 0 ? fmtMoney(margeBien) : '—'}
                            </td>
                            <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: margeBienColor }}>
                              {coutTot > 0 && margePctBien != null ? `${margePctBien.toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #c8dfc8', background: 'linear-gradient(180deg,#edf7ed,#e4f2e4)' }}>
                        <td colSpan={isMultiProjet ? 9 : 8} style={{ ...TD, fontWeight: 800, color: C.primary, letterSpacing: 0.5 }}>
                          TOTAL ({filteredBiens.length.toLocaleString('fr-FR')} bien{filteredBiens.length !== 1 ? 's' : ''})
                        </td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#4a505c' }}>{fmtN(totalSuperficieBiens)}</td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: totalPrixVenteBiens > 0 ? '#4a505c' : '#ccc' }}>
                          {totalPrixVenteBiens > 0 ? fmtMoney(totalPrixVenteBiens) : '—'}
                        </td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totalCaBiens > 0 ? C.primary : '#ccc' }}>
                          {totalCaBiens > 0 ? fmtMoney(totalCaBiens) : '—'}
                        </td>
                        <td colSpan={7} style={{ ...TD }}></td>
                        <td style={{ ...TD }}></td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totalCoutTotalBiens > 0 ? C.primary : '#ccc' }}>
                          {totalCoutTotalBiens > 0 ? fmtMoney(totalCoutTotalBiens) : '—'}
                        </td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totalMargeBiens > 0 ? '#2e7d32' : (totalMargeBiens < 0 ? '#c62828' : '#ccc') }}>
                          {totalCoutTotalBiens > 0 && totalCaBiens > 0 ? fmtMoney(totalMargeBiens) : '—'}
                        </td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totalMargeBiens > 0 ? '#2e7d32' : (totalMargeBiens < 0 ? '#c62828' : '#ccc') }}>
                          {totalCoutTotalBiens > 0 && totalMargePctBiens != null ? `${totalMargePctBiens.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Pagination détail */}
                {filteredBiens.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #ede9e4', background: '#faf9f7', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                        <span style={{ color: '#4a505c', fontWeight: 600 }}>
                          {(pageDetail - 1) * pageSizeDetail + 1}–{Math.min(pageDetail * pageSizeDetail, filteredBiens.length)}
                        </span>{' '}sur {filteredBiens.length}
                      </span>
                      <select
                        value={pageSizeDetail}
                        onChange={e => { setPageSizeDetail(Number(e.target.value)); setPageDetail(1); }}
                        style={{ height: 22, borderRadius: 4, border: '1px solid #d4c8bc', fontSize: '0.68rem', color: '#4a505c', background: '#fff', padding: '0 4px', cursor: 'pointer' }}
                      >
                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                      </select>
                    </div>
                    {filteredBiens.length > pageSizeDetail && (
                      <div style={{ display: 'flex', gap: 3 }}>
                        {[{ l: '«', t: 1 }, { l: '‹', t: pageDetail - 1 }, { l: '›', t: pageDetail + 1 }, { l: '»', t: totalPagesDetail }].map(({ l, t }) => (
                          <button key={l}
                            onClick={() => setPageDetail(Math.max(1, Math.min(totalPagesDetail, t)))}
                            disabled={t < 1 || t > totalPagesDetail || t === pageDetail}
                            style={{ minWidth: 26, height: 24, border: '1px solid #d4c8bc', borderRadius: 5, background: t === pageDetail ? '#4a505c' : '#fff', color: t === pageDetail ? '#fff' : '#4a505c', cursor: (t < 1 || t > totalPagesDetail || t === pageDetail) ? 'default' : 'pointer', fontSize: '0.72rem', opacity: (t < 1 || t > totalPagesDetail) ? 0.3 : 1 }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Tableau CA par mois (signature notaire) ───────────── */}
            <div style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a505c', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Chiffre d'affaires par mois ({DATE_FIELD_LABEL[moisFieldId]})
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <UnitToggle value={unitScale} onChange={setUnitScale} />
                  <button
                    onClick={exportExcelCAMois}
                    disabled={caParMois.length === 0}
                    style={{ height: 30, padding: '0 14px', borderRadius: 6, cursor: caParMois.length === 0 ? 'not-allowed' : 'pointer', background: '#fff', color: '#2e7d32', border: '1.5px solid #a5d6a7', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: caParMois.length === 0 ? 0.5 : 1 }}
                  >
                    ⬇ Excel
                  </button>
                </div>
              </div>

              {trancheSummary.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {trancheSummary.map(s => (
                    <TrancheChip
                      key={s.key}
                      label={isMultiProjet ? `${s.projet_nom} — ${s.tranche}` : s.tranche}
                      value={Number(s.nb).toLocaleString('fr-FR')}
                      active={activeTranchesMois.has(s.key)}
                      onClick={() => toggleTrancheMois(s.key)}
                    />
                  ))}
                  {activeTranchesMois.size > 0 && (
                    <button
                      onClick={() => setActiveTranchesMois(new Set())}
                      style={{ fontSize: '0.62rem', fontWeight: 700, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
                    >
                      ✕ Réinitialiser
                    </button>
                  )}
                </div>
              )}

              {caParMois.length > 0 ? (
                <div style={{ borderRadius: 8, border: '1.5px solid #ddd8d0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem' }}>
                      <thead>
                        <tr style={{ background: 'linear-gradient(180deg,#f7f5f2,#f0ede8)' }}>
                          {isMultiProjet && <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left' }}>Projet</th>}
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'left', borderRight: '1px solid #ddd8d0', paddingLeft: 14 }}>Mois ({DATE_FIELD_LABEL[moisFieldId]})</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Nb biens</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right' }}>Superficie (m²)</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary }} title="Coût/m² moyen pondéré selon les tranches du mois">Coût/m² moyen</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary }}>Coût total</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', borderLeft: '1px solid #ddd8d0', color: C.primary, background: '#edf7ed' }}>CA (contrat.PRIX_NET)</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary, background: '#edf7ed' }}>Marge</th>
                          <th style={{ ...TH, fontSize: '0.58rem', textAlign: 'right', color: C.primary, paddingRight: 14, background: '#edf7ed' }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caParMois.map((l, i) => {
                          const marge = l.ca_totale - l.montant_estime;
                          const margePct = l.ca_totale > 0 ? (marge / l.ca_totale) * 100 : null;
                          const margeColor = marge > 0 ? '#2e7d32' : (marge < 0 ? '#c62828' : '#ccc');
                          const coutMoyen = l.superficie_totale > 0 ? l.montant_estime / l.superficie_totale : 0;
                          return (
                            <tr key={l.key} style={{ background: i % 2 === 0 ? '#fff' : '#faf9f8' }}>
                              {isMultiProjet && <td style={{ ...TD, fontSize: '0.68rem', color: '#888' }}>{l.projet_nom}</td>}
                              <td style={{ ...TD, fontSize: '0.68rem', fontWeight: 700, color: '#3d3d3d', borderRight: '1px solid #ede9e4', paddingLeft: 14, textTransform: 'capitalize' }}>
                                {monthLabel(l.mois)}
                              </td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}><ClickNum list={l.list} prefix="CA par mois" title={`${monthLabel(l.mois)}${isMultiProjet ? ` · ${l.projet_nom}` : ''}`}>{l.nb_biens.toLocaleString('fr-FR')}</ClickNum></td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: '#666' }}><ClickNum list={l.list} prefix="CA par mois" title={`${monthLabel(l.mois)}${isMultiProjet ? ` · ${l.projet_nom}` : ''}`}>{fmtN(l.superficie_totale)}</ClickNum></td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', color: coutMoyen > 0 ? '#666' : '#ccc', fontStyle: 'italic' }}>
                                {coutMoyen > 0 ? fmtN(coutMoyen) : '—'}
                              </td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: l.montant_estime > 0 ? 700 : 400, color: l.montant_estime > 0 ? C.primary : '#ccc' }}>
                                <ClickNum list={l.list} prefix="CA par mois" title={`${monthLabel(l.mois)}${isMultiProjet ? ` · ${l.projet_nom}` : ''}`}>{l.montant_estime > 0 ? fmtMoney(l.montant_estime) : '—'}</ClickNum>
                              </td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, borderLeft: '1px solid #ede9e4', color: l.ca_totale > 0 ? C.primary : '#ccc', background: l.ca_totale > 0 ? '#f8fdf8' : 'transparent' }}>
                                <ClickNum list={l.list} prefix="CA par mois" title={`${monthLabel(l.mois)}${isMultiProjet ? ` · ${l.projet_nom}` : ''}`}>{l.ca_totale > 0 ? fmtMoney(l.ca_totale) : '—'}</ClickNum>
                              </td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, color: margeColor, background: '#f8fdf8' }}>
                                {l.montant_estime > 0 && l.ca_totale > 0 ? fmtMoney(marge) : '—'}
                              </td>
                              <td style={{ ...TD, fontSize: '0.68rem', textAlign: 'right', fontWeight: 700, color: margeColor, paddingRight: 14, background: '#f8fdf8' }}>
                                {l.montant_estime > 0 && margePct != null ? `${margePct.toFixed(1)}%` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #c8dfc8', background: 'linear-gradient(180deg,#edf7ed,#e4f2e4)' }}>
                          {isMultiProjet && <td style={{ ...TD }}></td>}
                          <td style={{ ...TD, fontSize: '0.65rem', fontWeight: 800, color: C.primary, borderRight: '1px solid #c8dfc8', paddingLeft: 14, letterSpacing: 0.5 }}>TOTAL</td>
                          <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}><ClickNum list={caParMois.flatMap(m => m.list)} prefix="CA par mois" title={"Total"}>{totalBiensMois.toLocaleString('fr-FR')}</ClickNum></td>
                          <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: '#4a505c' }}><ClickNum list={caParMois.flatMap(m => m.list)} prefix="CA par mois" title={"Total"}>{fmtN(totalSuperficieMois)}</ClickNum></td>
                          <td style={{ ...TD }}></td>
                          <td style={{ ...TD, fontSize: '0.65rem', textAlign: 'right', fontWeight: 700, color: totalMontantMois > 0 ? C.primary : '#ccc' }}>
                            <ClickNum list={caParMois.flatMap(m => m.list)} prefix="CA par mois" title={"Total"}>{totalMontantMois > 0 ? fmtMoney(totalMontantMois) : '—'}</ClickNum>
                          </td>
                          <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, borderLeft: '1px solid #c8dfc8', color: totalCAMois > 0 ? C.primary : '#ccc' }}>
                            <ClickNum list={caParMois.flatMap(m => m.list)} prefix="CA par mois" title={"Total"}>{totalCAMois > 0 ? fmtMoney(totalCAMois) : '—'}</ClickNum>
                          </td>
                          <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, color: totalMargeMois > 0 ? '#2e7d32' : (totalMargeMois < 0 ? '#c62828' : '#ccc') }}>
                            {totalMontantMois > 0 && totalCAMois > 0 ? fmtMoney(totalMargeMois) : '—'}
                          </td>
                          <td style={{ ...TD, fontSize: '0.72rem', textAlign: 'right', fontWeight: 800, paddingRight: 14, color: totalMargeMois > 0 ? '#2e7d32' : (totalMargeMois < 0 ? '#c62828' : '#ccc') }}>
                            {totalMontantMois > 0 && totalMargePctMois != null ? `${totalMargePctMois.toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', padding: 30 }}>
                  Aucun bien avec une date de {DATE_FIELD_LABEL[moisFieldId]} renseignée pour cette sélection.
                </p>
              )}
            </div>

            {caComptaDetail && (
              <BienDetailPopup detail={caComptaDetail} showProjet={isMultiProjet} onClose={closeCaComptaDetail} />
            )}

          </>
        )}

        {!loading && !detail.length && selectedProjetIds.length > 0 && !error && (
          <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', padding: 30 }}>
            Aucune tranche trouvée pour {selectedProjetIds.length > 1 ? 'ces projets' : 'ce projet'}.
          </p>
        )}

        {!loading && selectedProjetIds.length === 0 && (
          <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', padding: 40 }}>
            Sélectionnez un ou plusieurs projets pour afficher les données.
          </p>
        )}
      </div>
    </div>
  );
}
